import { useState, useEffect, useRef, Fragment } from "react";
import LoadingButton from "../../../components/BaseComponents/LoadingButton/LoadingButton";
import {
  CreditCard,
  ArrowUpRight,
  ArrowDownLeft,
  Plus,
  ExternalLink,
  MoreHorizontal,
  Upload,
  X,
  Info,
  Check,
  Plane,
  Hotel,
  Bus,
  Shield,
  Search,
  Filter,
  Download,
  Eye,
  Pencil,
  Trash2,
  FileText,
  Calendar,
  RefreshCw,
  Copy,
  Printer,
  Loader2,
  AlertCircle,
  Users,
  ChevronDown,
  ChevronRight,
  Split,
} from "lucide-react";
import styles from "./PaymentsTab.module.css";
import DeleteConfirmModal from "../../../components/BaseComponents/DeleteConfirmModal/DeleteConfirmModal";
import {
  useGetBookingPayments,
  useCreatePayment,
  useUpdatePayment,
  useDeletePayment,
  toApiMethod,
  toApiLinkedTo,
  toApiStatus,
  toApiPaymentType,
  toDisplayPaymentType,
  toDisplayDirection,
  toDisplayClientType,
} from "../../../api/finance/usePaymentsApi";
import { getAccounts } from "../../../api/finance/accounts";
import { useGetProductModifications } from "../../../api/booking/useBookingApi";
import { useToast } from "../../../context/ToastContext";
import SupplierPaymentWizard from "../../../components/Booking/SupplierPaymentWizard";

/* ── helpers ── */
const fmt = (n) =>
  `€ ${Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })}`;

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "—";
  }
};

const PAYMENT_METHOD_ICONS = {
  Stripe: { icon: "S", bg: "#635bff", color: "#fff" },
  "Bank Transfer": { icon: "🏦", bg: "#e8f0fe", color: "#3b5998" },
  SEPA: { icon: "🏦", bg: "#e8f0fe", color: "#3b5998" },
  SWIFT: { icon: "🌐", bg: "#fef3c7", color: "#92400e" },
  Visa: { icon: "V", bg: "#1a1f71", color: "#fff" },
  Mastercard: { icon: "M", bg: "#eb001b", color: "#fff" },
  Cash: { icon: "💵", bg: "#f0fdf4", color: "#16a34a" },
  PayPal: { icon: "P", bg: "#003087", color: "#fff" },
  Amex: { icon: "A", bg: "#006fcf", color: "#fff" },
  "Payment Terminal": { icon: "T", bg: "#0f766e", color: "#fff" },
};

const METHOD_TO_API_KEY = {
  "Bank Transfer": "bank_transfer",
  SEPA: "sepa",
  SWIFT: "swift",
  "Direct Debit": "direct_debit",
  Cash: "cash",
  Visa: "visa",
  Mastercard: "mastercard",
  Amex: "amex",
  Stripe: "stripe",
  PayPal: "paypal",
  "Payment Terminal": "payment_terminal",
};

const CLIENT_PAYMENT_METHODS = [
  { label: "Bank Transfer", group: "Bank" },
  { label: "SEPA", group: "Bank" },
  { label: "Visa", group: "Card" },
  { label: "Mastercard", group: "Card" },
  { label: "Amex", group: "Card" },
  { label: "Stripe", group: "Card" },
  { label: "PayPal", group: "Card" },
  { label: "Payment Terminal", group: "Card" },
  { label: "Cash", group: "Cash" },
];

const SUPPLIER_PAYMENT_METHODS = [
  { label: "Bank Transfer", group: "Bank" },
  { label: "SEPA", group: "Bank" },
  { label: "SWIFT", group: "Bank" },
  { label: "Stripe", group: "Card" },
];

const getMethodIcon = (method) => {
  const m = PAYMENT_METHOD_ICONS[method];
  if (!m) return { icon: "💳", bg: "#f5f6f8", color: "#6b7280" };
  return m;
};

const STATUS_MAP = {
  Paid: { label: "Paid", cls: "statusPaid" },
  Completed: { label: "Completed", cls: "statusPaid" },
  Partial: { label: "Partially Paid", cls: "statusPartial" },
  "Partially Paid": { label: "Partially Paid", cls: "statusPartial" },
  Unpaid: { label: "Not Paid", cls: "statusUnpaid" },
  "Not Paid": { label: "Not Paid", cls: "statusUnpaid" },
  Pending: { label: "Pending", cls: "statusPartial" },
  // Cancellation refund workflow (cancelled bookings only)
  Refunded: { label: "Refunded", cls: "statusRefunded" },
  "Waiting Supplier Refund": { label: "Waiting Supplier Refund", cls: "statusWaiting" },
};

const statusBadge = (s) => {
  const info = STATUS_MAP[s] || { label: s || "Unknown", cls: "statusUnpaid" };
  return <span className={styles[info.cls]}>{info.label}</span>;
};

const PAYMENT_STATUS_OPTIONS = ["Pending", "Completed", "Failed", "Cancelled", "Refunded"];

const CLIENT_PAYMENT_TYPE_OPTIONS = [
  { value: "Incoming Payment", label: "Incoming Payment", direction: "Incoming", icon: "↓" },
  { value: "Outgoing Payment", label: "Outgoing Payment", direction: "Outgoing", icon: "↑" },
];

const SUPPLIER_PAYMENT_TYPE_OPTIONS = [
  { value: "Incoming Payment", label: "Incoming Payment", direction: "Incoming", icon: "↓" },
  { value: "Outgoing Payment", label: "Outgoing Payment", direction: "Outgoing", icon: "↑" },
  { value: "Refund", label: "Refund", direction: "Outgoing", icon: "↩" },
];

const paymentTypeBadge = (type, direction, clientContext = false) => {
  if (!type && !direction) return null;
  const isIncoming = (direction || "").toLowerCase() === "incoming" ||
    (type || "").toLowerCase().includes("incoming");
  const isRefund = (type || "").toLowerCase().includes("refund");
  // A client-context outgoing payout is shown red (money out of the client ledger);
  // supplier outgoing stays blue.
  const cls = isRefund ? "typeBadgeRefund"
    : isIncoming ? "typeBadgeIncoming"
    : clientContext ? "typeBadgeOutgoingClient" : "typeBadgeOutgoing";
  const icon = isRefund ? "↩" : isIncoming ? "↓" : "↑";
  const label = type || (isIncoming ? "Incoming" : "Outgoing");
  return (
    <span className={styles[cls]}>
      <span className={styles.typeBadgeIcon}>{icon}</span>
      {label}
    </span>
  );
};

const SUPPLIER_LOGOS = {
  hotelbeds: { text: "hotelbeds", bold: true, bg: "#fff", color: "#d4451a" },
  transferz: { text: "Transferz", icon: "🚌", bg: "#eef3ff", color: "#3b5bdb" },
  "sky airline": { text: "Sky Airline", icon: "S✈Y", bg: "#fff", color: "#2563eb" },
  "insurance co": { text: "Insurance Co", icon: "🛡️", bg: "#f0fdf4", color: "#16a34a" },
};

/* ── Demo Data (will be replaced by real API data) ── */
const DEMO_CLIENT_PAYMENTS = [
  {
    id: 1,
    date: "2026-05-20",
    method: "Stripe",
    reference: "pi_3N8xJ2mY7K",
    amount: 1200,
    currency: "EUR",
    linkedTo: "Deposit",
    status: "Completed",
    notes: "Initial deposit payment via Stripe checkout",
    createdBy: "System (Online)",
  },
  {
    id: 2,
    date: "2026-05-24",
    method: "Bank Transfer",
    reference: "BE68539007547034",
    amount: 850,
    currency: "EUR",
    linkedTo: "Balance Payment",
    status: "Completed",
    notes: "Balance payment received",
    createdBy: "Admin",
  },
  {
    id: 3,
    date: "2026-05-28",
    method: "Visa",
    reference: "ch_3PqR5x2eZv",
    amount: 400,
    currency: "EUR",
    linkedTo: "Booking Payment",
    status: "Completed",
    notes: "",
    createdBy: "System (Online)",
  },
  {
    id: 4,
    date: "2026-06-01",
    method: "SEPA",
    reference: "SEPA-DD-2026-0601",
    amount: 320,
    currency: "EUR",
    linkedTo: "Balance Payment",
    status: "Pending",
    notes: "SEPA direct debit — processing (3-5 business days)",
    createdBy: "Admin",
  },
  {
    id: 5,
    date: "2026-06-05",
    method: "Bank Transfer",
    reference: "—",
    amount: 680,
    currency: "EUR",
    linkedTo: "Balance Payment",
    status: "Unpaid",
    notes: "Final balance — awaiting client transfer",
    createdBy: "Admin",
  },
];

const DEMO_SUPPLIER_PAYMENTS = [
  {
    id: 1,
    date: "2026-05-21",
    supplier: "Hotelbeds",
    supplierKey: "hotelbeds",
    products: [
      { name: "Grand Hotel Barcelona", type: "Hotel" },
      { name: "City Resort Madrid", type: "Hotel" },
    ],
    reference: "HB-INV-2024-56789",
    method: "Bank Transfer",
    amount: 800,
    paidAmount: 800,
    currency: "EUR",
    status: "Paid",
    notes: "Hotel reservation confirmed",
  },
  {
    id: 2,
    date: "2026-05-22",
    supplier: "Transferz",
    supplierKey: "transferz",
    products: [{ name: "Airport Transfer BCN", type: "Transfer" }],
    reference: "TRF-456789",
    method: "SEPA",
    amount: 150,
    paidAmount: 150,
    currency: "EUR",
    status: "Paid",
    notes: "",
  },
  {
    id: 3,
    date: "2026-05-25",
    supplier: "Sky Airline",
    supplierKey: "sky airline",
    products: [
      { name: "BRU → BCN (Outbound)", type: "Flight" },
      { name: "BCN → BRU (Return)", type: "Flight" },
    ],
    reference: "SKY-INV-98765",
    method: "Bank Transfer",
    amount: 700,
    paidAmount: 200,
    currency: "EUR",
    status: "Partially Paid",
    notes: "Partial payment — remainder due before departure",
  },
  {
    id: 4,
    date: "2026-05-27",
    supplier: "Insurance Co",
    supplierKey: "insurance co",
    products: [{ name: "Travel Insurance Premium", type: "Insurance" }],
    reference: "INS-POL-12345",
    method: "SWIFT",
    amount: 200,
    paidAmount: 0,
    currency: "EUR",
    status: "Not Paid",
    notes: "Awaiting invoice from supplier",
  },
  {
    id: 5,
    date: "2026-05-28",
    supplier: "Hotelbeds",
    supplierKey: "hotelbeds",
    products: [{ name: "Grand Hotel Barcelona — Extra Night", type: "Hotel" }],
    reference: "HB-INV-2024-56801",
    method: "Bank Transfer",
    amount: 180,
    paidAmount: 0,
    currency: "EUR",
    status: "Not Paid",
    notes: "Additional night added by client",
  },
];

/* ===================================================================
   AddClientPaymentModal
   =================================================================== */
const AddClientPaymentModal = ({ open, onClose, onSave, editData, saving = false, bookingRef = "", outstandingAmount = 0, bookingTotal = 0, clientPaid = 0, customerName = "", refundDue = 0 }) => {
  const [form, setForm] = useState({
    paymentType: "",
    status: "",
    method: "",
    provider: "",
    date: new Date().toISOString().slice(0, 10),
    currency: "EUR",
    accountId: "",
    accountNumber: "",
    grossAmount: "",
    transactionFee: "",
    clientCoversFee: true,
    settlementReference: "",
    notes: "",
  });

  useEffect(() => {
    if (open && editData) {
      setForm({
        paymentType: editData.paymentTypeDisplay || toDisplayPaymentType(editData.paymentType) || "Incoming Payment",
        status: editData.status || "Pending",
        method: editData.method || "",
        provider: editData.provider || "",
        date: editData.date || new Date().toISOString().slice(0, 10),
        currency: editData.currency || "EUR",
        accountId: editData.accountId ? String(editData.accountId) : "",
        accountNumber: editData.accountNumber || "",
        grossAmount: editData.grossAmount != null ? String(editData.grossAmount)
          : (editData.amount != null ? String(editData.amount) : ""),
        transactionFee: editData.transactionFee != null ? String(editData.transactionFee) : "",
        clientCoversFee: editData.clientCoversFee !== false,
        settlementReference: editData.settlementReference || "",
        notes: editData.notes || "",
      });
    } else if (open && !editData) {
      // Pre-fill the gross amount with the client's outstanding balance (still editable).
      // Decimal comma, no thousand separators — the field parses via replace(",", ".").
      const prefillGross = Number(outstandingAmount) > 0
        ? Number(outstandingAmount).toFixed(2).replace(".", ",")
        : "";
      setForm({
        paymentType: "",
        status: "",
        method: "",
        provider: "",
        date: new Date().toISOString().slice(0, 10),
        currency: "EUR",
        accountId: "",
        accountNumber: "",
        grossAmount: prefillGross,
        transactionFee: "",
        clientCoversFee: true,
        settlementReference: "",
        notes: "",
      });
    }
  }, [open, editData, bookingRef, outstandingAmount]);

  const [fieldErrors, setFieldErrors] = useState({});
  const [allClientAccounts, setAllClientAccounts] = useState([]);

  useEffect(() => {
    if (!open) return;
    getAccounts({ usage: "client", active: true, limit: 100 })
      .then((res) =>
        setAllClientAccounts(
          (res.data || []).map((a) => ({
            value: String(a.id),
            label: a.account_name,
            linkedPaymentMethods: a.linked_payment_methods || [],
            accountType: a.account_type,
            accountNumber: a.account_number || "",
          }))
        )
      )
      .catch(() => setAllClientAccounts([]));
  }, [open]);

  const filteredClientAccounts = allClientAccounts.filter((acc) => {
    if (!form.method) return true;
    const apiKey = METHOD_TO_API_KEY[form.method];
    if (!apiKey) return true;
    return (acc.linkedPaymentMethods || []).includes(apiKey);
  });

  if (!open) return null;

  const set = (k, v) => {
    setForm((p) => {
      const next = { ...p, [k]: v };
      if (k === "method") { next.accountId = ""; next.accountNumber = ""; }
      if (k === "accountId") {
        const selectedAcc = filteredClientAccounts.find((a) => a.value === v);
        next.accountNumber = selectedAcc?.accountNumber || "";
      }
      // Outgoing payments have no provider fee — drop any stale fee value
      if (k === "paymentType" && v === "Outgoing Payment") next.transactionFee = "";
      return next;
    });
    if (fieldErrors[k]) setFieldErrors((p) => { const n = { ...p }; delete n[k]; return n; });
  };

  const notesLen = (form.notes || "").length;
  const showAccountNumber = form.method === "Bank Transfer" || form.method === "SEPA" || form.method === "SWIFT";
  const accountHasNumber = !!form.accountId && !!form.accountNumber;

  // Outgoing = money paid back to the client (payout). No provider fee / "net received" applies there —
  // those only make sense for money coming IN from a client through a payment provider.
  const isOutgoing = form.paymentType === "Outgoing Payment";

  // ── SUNSKY client payment logic: Gross − Transaction Cost = Net received.
  //    The NET is what settles the booking — the client covers the payment-method
  //    cost by paying extra, so it is deducted. ──
  const grossNum = parseFloat(String(form.grossAmount).replace(",", ".")) || 0;
  const feeNum = isOutgoing ? 0 : (parseFloat(String(form.transactionFee).replace(",", ".")) || 0);
  const netReceived = Math.max(0, grossNum - feeNum);

  const moneyFmt = (n) => Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ── Booking summary (DUAL rule) — a client-covered transaction cost is part of
  //    what the client owes, so it settles the booking only NET (gross − covered
  //    cost) and thus lifts outstanding; a Sunsky-borne cost is absorbed (net =
  //    gross). Outstanding + credit both measure against the net pool. An outgoing
  //    payout subtracts its full amount. Outstanding clamped at 0; surplus = credit. ──
  const priorPaid = Number(clientPaid) || 0; // net applied so far (capped at total)
  const bookingTot = Number(bookingTotal) || 0;
  const coveredFee = form.clientCoversFee !== false ? feeNum : 0;
  const settleThis = isOutgoing ? -grossNum : (grossNum - coveredFee); // net that settles the booking
  const netAfter = Math.max(0, priorPaid + settleThis);
  const appliedAfter = Math.min(netAfter, bookingTot);
  const outstandingAfter = Math.max(0, bookingTot - netAfter);
  const creditAfter = Math.max(0, netAfter - bookingTot);
  const paidAfter = appliedAfter;

  // The live summary is a preview — only show the numeric cards once an amount is actually entered.
  const hasAmount = grossNum > 0;

  const validateAndSubmit = () => {
    const errs = {};
    if (!form.paymentType) errs.paymentType = "Payment type is required";
    if (!form.status) errs.status = "Payment status is required";
    if (!form.method) errs.method = "Payment method is required";
    if (!form.date) errs.date = "Payment date is required";
    if (!form.accountId) errs.accountId = "Account is required";
    if (showAccountNumber && !accountHasNumber && !form.accountNumber.trim()) errs.accountNumber = "Account number is required for bank transfers";
    if (!form.grossAmount || grossNum <= 0) errs.grossAmount = isOutgoing ? "Amount must be greater than 0" : "Client amount must be greater than 0";
    if (!isOutgoing && feeNum > grossNum) errs.transactionFee = "Transaction cost cannot exceed the client amount";
    if (Object.keys(errs).length > 0) { setFieldErrors(errs); return; }
    onSave && onSave({
      ...form,
      // gross stored as amount; the NET (gross − cost) settles the booking
      amount: grossNum,
      grossAmount: grossNum,
      transactionFee: feeNum,
      // Whether the client covers the cost (default true). Only meaningful with a fee.
      clientCoversFee: feeNum > 0 ? (form.clientCoversFee !== false) : true,
      netReceived,
      provider: (form.provider || "").trim(),
      settlementReference: (form.settlementReference || "").trim(),
      // Linked To removed from the UI — default so the existing handler stays valid
      linkedTo: "Booking Payment",
    });
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={`${styles.modal} ${styles.modalXWide}`} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div>
            <h3 className={styles.modalTitle}>{editData ? "Edit Client Payment" : "Add Client Payment"}</h3>
            <p className={styles.modalSubtitle}>
              {editData ? "Update the details of this client payment." : "Record a payment received from a client."}
            </p>
          </div>
          <button className={styles.modalClose} onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.payTwoCol}>
            {/* ─────────── LEFT: FORM ─────────── */}
            <div className={styles.payFormCol}>
              {/* Customer & Booking Reference Banner */}
              <div className={styles.clientBanner}>
                <div className={styles.clientBannerAvatar}>
                  {(customerName || "C").charAt(0).toUpperCase()}
                </div>
                <div className={styles.clientBannerInfo}>
                  <span className={styles.clientBannerBooking}>
                    BOOKING NUMBER: <span className={styles.clientBannerRefNum}>{bookingRef || "—"}</span>
                  </span>
                  <span className={styles.clientBannerName}>Client: {customerName || "—"}</span>
                </div>
              </div>

              {/* Payment Type */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Payment Type <span className={styles.req}>*</span>
                </label>
                <div className={styles.selectWithIcon}>
                  <span
                    className={styles.typeDot}
                    style={{
                      background: form.paymentType === "Incoming Payment" ? "#e8f5ee" : form.paymentType === "Refund" ? "#fef3e2" : form.paymentType === "Outgoing Payment" ? "#eef3ff" : "#f5f6f8",
                      color: form.paymentType === "Incoming Payment" ? "#0d7a4f" : form.paymentType === "Refund" ? "#b06e0a" : form.paymentType === "Outgoing Payment" ? "#2563eb" : "#9ca3af",
                    }}
                  >
                    {form.paymentType === "Incoming Payment" ? "↓" : form.paymentType === "Refund" ? "↩" : form.paymentType === "Outgoing Payment" ? "↑" : "—"}
                  </span>
                  <select
                    className={styles.formSelect}
                    style={fieldErrors.paymentType ? { border: "1.5px solid #ef4444" } : undefined}
                    value={form.paymentType}
                    onChange={(e) => set("paymentType", e.target.value)}
                    disabled={!!editData}
                  >
                    <option value="">Select Payment Type...</option>
                    {CLIENT_PAYMENT_TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                {fieldErrors.paymentType && <div className={styles.fieldError}>{fieldErrors.paymentType}</div>}
                {editData && <span className={styles.formHint}>Payment type cannot be changed after creation</span>}
              </div>

              {/* Payment Status */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Payment Status <span className={styles.req}>*</span>
                </label>
                <select
                  className={styles.formSelect}
                  style={fieldErrors.status ? { border: "1.5px solid #ef4444" } : undefined}
                  value={form.status}
                  onChange={(e) => set("status", e.target.value)}
                >
                  <option value="">Select Payment Status...</option>
                  {PAYMENT_STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                {fieldErrors.status && <div className={styles.fieldError}>{fieldErrors.status}</div>}
              </div>

              {/* Payment Method */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Payment Method <span className={styles.req}>*</span>
                </label>
                <div className={styles.selectWithIcon}>
                  <span
                    className={styles.methodDot}
                    style={{
                      background: getMethodIcon(form.method).bg,
                      color: getMethodIcon(form.method).color,
                    }}
                  >
                    {getMethodIcon(form.method).icon}
                  </span>
                  <select
                    className={styles.formSelect}
                    style={fieldErrors.method ? { border: "1.5px solid #ef4444" } : undefined}
                    value={form.method}
                    onChange={(e) => set("method", e.target.value)}
                  >
                    <option value="">Select Payment Method...</option>
                    {CLIENT_PAYMENT_METHODS.map((m) => (
                      <option key={m.label} value={m.label}>{m.label}</option>
                    ))}
                  </select>
                </div>
                {fieldErrors.method && <div className={styles.fieldError}>{fieldErrors.method}</div>}
              </div>

              {/* Payment Provider (optional) — who processed it: Worldline, Stripe, Mollie… */}
              {!isOutgoing && (
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Payment Provider</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    list="client-payment-providers"
                    placeholder="e.g. Worldline, Stripe, Mollie"
                    value={form.provider}
                    onChange={(e) => set("provider", e.target.value)}
                  />
                  <datalist id="client-payment-providers">
                    <option value="Worldline" />
                    <option value="Stripe" />
                    <option value="Mollie" />
                    <option value="Adyen" />
                    <option value="PayPal" />
                    <option value="Bank" />
                  </datalist>
                  <span className={styles.formHint}>The provider that processed this payment (used for fee reconciliation).</span>
                </div>
              )}

              {/* Payment Date & Currency */}
              <div className={styles.formRow2}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Payment Date <span className={styles.req}>*</span>
                  </label>
                  <input
                    type="date"
                    className={styles.formInput}
                    style={fieldErrors.date ? { border: "1.5px solid #ef4444" } : undefined}
                    value={form.date}
                    onChange={(e) => set("date", e.target.value)}
                  />
                  {fieldErrors.date && <div className={styles.fieldError}>{fieldErrors.date}</div>}
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Currency <span className={styles.req}>*</span>
                  </label>
                  <select
                    className={styles.formSelect}
                    value={form.currency}
                    onChange={(e) => set("currency", e.target.value)}
                  >
                    <option value="EUR">EUR - Euro</option>
                    <option value="USD">USD - US Dollar</option>
                    <option value="GBP">GBP - British Pound</option>
                  </select>
                </div>
              </div>

              {/* Account */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Account <span className={styles.req}>*</span>
                </label>
                <select
                  className={styles.formSelect}
                  style={fieldErrors.accountId ? { border: "1.5px solid #ef4444" } : undefined}
                  value={form.accountId}
                  onChange={(e) => set("accountId", e.target.value)}
                >
                  <option value="">Select Account...</option>
                  {filteredClientAccounts.map((acc) => (
                    <option key={acc.value} value={acc.value}>{acc.label}</option>
                  ))}
                </select>
                {fieldErrors.accountId && <div className={styles.fieldError}>{fieldErrors.accountId}</div>}
                {form.method && filteredClientAccounts.length === 0 && (
                  <span className={styles.formHint} style={{ color: "#b06e0a" }}>No accounts linked to {form.method}. Add one in Finance → Accounts.</span>
                )}
                {!fieldErrors.accountId && <span className={styles.formHint}>Select the account where the payment will be recorded.</span>}
              </div>

              {/* Account Number / IBAN (bank methods only) */}
              {showAccountNumber && (
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Account Number / IBAN <span className={styles.req}>*</span>
                  </label>
                  <input
                    type="text"
                    className={`${styles.formInput}${fieldErrors.accountNumber ? ` ${styles.inputError}` : ""}${accountHasNumber ? ` ${styles.inputReadonly}` : ""}`}
                    placeholder="e.g., BE68 5390 0754 7034"
                    value={form.accountNumber}
                    onChange={(e) => { if (!accountHasNumber) set("accountNumber", e.target.value); }}
                    readOnly={accountHasNumber}
                  />
                  {fieldErrors.accountNumber && <div className={styles.fieldError}>{fieldErrors.accountNumber}</div>}
                  <span className={styles.formHint}>{accountHasNumber ? "Auto-filled from the selected account" : "Enter the bank account number or IBAN"}</span>
                </div>
              )}

              {/* ─── PAYMENT AMOUNTS ─── */}
              <div className={styles.sectionHeading}>{isOutgoing ? "Payment Amount" : "Payment Amounts"}</div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  {isOutgoing ? "Amount" : "Client Amount (Gross)"} <span className={styles.req}>*</span>
                </label>
                {/* Refund-due suggestion: for an Outgoing (payout) payment, pre-fill the
                    exact amount we owe the client from the current booking balance so the
                    user doesn't have to look it up. Only shown when a refund is actually
                    due (customer overpaid or booking cancelled with credit remaining). */}
                {isOutgoing && refundDue > 0.005 && (
                  <button
                    type="button"
                    onClick={() => set("grossAmount", Number(refundDue).toFixed(2).replace(".", ","))}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      margin: "0 0 8px 0", padding: "6px 10px",
                      background: "#fef3e2", border: "1px solid #f5d9a8",
                      borderRadius: 6, cursor: "pointer",
                      fontSize: 12, color: "#8a4b0a", fontWeight: 600,
                    }}
                    title="Use the refund amount currently due to the customer"
                  >
                    <span style={{ fontSize: 14 }}>↩</span>
                    Refund due to customer: &euro; {Number(refundDue).toFixed(2).replace(".", ",")} — click to use
                  </button>
                )}
                <div className={styles.inputWithPrefix}>
                  <span className={styles.inputPrefix}>&euro;</span>
                  <input
                    type="text"
                    className={`${styles.formInput}${fieldErrors.grossAmount ? ` ${styles.inputError}` : ""}`}
                    placeholder="0,00"
                    value={form.grossAmount}
                    onChange={(e) => set("grossAmount", e.target.value)}
                  />
                </div>
                {fieldErrors.grossAmount && <div className={styles.fieldError}>{fieldErrors.grossAmount}</div>}
                <span className={styles.formHint}>{isOutgoing ? "Amount paid back to the client." : "Total amount paid by the client."}</span>
              </div>

              {/* Transaction fee & net received apply only to incoming provider payments */}
              {!isOutgoing && (
                <>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      Transaction Cost <Info size={13} className={styles.infoIcon} />
                    </label>
                    <div className={styles.inputWithPrefix}>
                      <span className={styles.inputPrefix}>&euro;</span>
                      <input
                        type="text"
                        className={`${styles.formInput}${fieldErrors.transactionFee ? ` ${styles.inputError}` : ""}`}
                        placeholder="0,00"
                        value={form.transactionFee}
                        onChange={(e) => set("transactionFee", e.target.value)}
                      />
                    </div>
                    {fieldErrors.transactionFee && <div className={styles.fieldError}>{fieldErrors.transactionFee}</div>}
                    <span className={styles.formHint}>Payment provider / terminal cost.</span>
                  </div>

                  {feeNum > 0 && (
                    <div className={styles.formGroup}>
                      <label className={styles.checkboxRow} style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", fontWeight: 500 }}>
                        <input
                          type="checkbox"
                          checked={form.clientCoversFee !== false}
                          onChange={(e) => set("clientCoversFee", e.target.checked)}
                          style={{ marginTop: 3, flexShrink: 0 }}
                        />
                        <span>
                          Client is covering the transaction cost
                          <span className={styles.formHint} style={{ display: "block", fontWeight: 400 }}>
                            {form.clientCoversFee !== false
                              ? "On — the client is billed this cost: it's added to what they owe, so the Outstanding rises by the cost."
                              : "Off — SUNSKY absorbs the cost; it doesn't change what the client owes (Outstanding unaffected)."}
                          </span>
                        </span>
                      </label>
                    </div>
                  )}

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      Net Received <span className={styles.req}>*</span>
                    </label>
                    <div className={`${styles.inputWithPrefix} ${styles.netReceivedBox}`}>
                      <span className={styles.inputPrefix}>&euro;</span>
                      <input
                        type="text"
                        className={`${styles.formInput} ${styles.netReceivedInput}`}
                        value={moneyFmt(netReceived)}
                        readOnly
                      />
                    </div>
                    <span className={styles.formHint}>Gross − cost = net received — this is what settles the booking.</span>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Settlement Reference</label>
                    <input
                      type="text"
                      className={styles.formInput}
                      placeholder="Provider settlement / payout batch reference"
                      value={form.settlementReference}
                      onChange={(e) => set("settlementReference", e.target.value)}
                    />
                    <span className={styles.formHint}>Used for CODA/CAMT bank reconciliation (optional).</span>
                  </div>
                </>
              )}

              {/* Notes & Attachments */}
              <div className={styles.formRow2}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Notes</label>
                  <textarea
                    className={styles.formTextarea}
                    rows={3}
                    maxLength={255}
                    placeholder="Add any notes about this payment..."
                    value={form.notes}
                    onChange={(e) => set("notes", e.target.value)}
                  />
                  <span className={styles.formCharCount}>{notesLen} / 255</span>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Attachments (Optional)</label>
                  <button className={styles.uploadBtn}>
                    <Upload size={14} /> Upload File
                  </button>
                  <span className={styles.formHint}>
                    Upload receipt, bank slip or payment proof (JPG, PNG, PDF – Max 10MB)
                  </span>
                </div>
              </div>
            </div>

            {/* ─────────── RIGHT: SUMMARY ─────────── */}
            <div className={styles.paySideCol}>
              {/* Numeric summary is a live preview — only appears once an amount is entered */}
              {!hasAmount && (
                <div className={`${styles.sumCard} ${styles.sumPlaceholder}`}>
                  <div className={styles.sumPlaceholderIcon}><FileText size={20} /></div>
                  <div className={styles.sumPlaceholderText}>
                    Enter a payment amount to preview the summary.
                  </div>
                </div>
              )}

              {/* Payment Summary — incoming provider payments only (fee/net) */}
              {hasAmount && !isOutgoing && (
                <div className={styles.sumCard}>
                  <div className={styles.sumCardHeader}>
                    <FileText size={15} />
                    <span>Payment Summary</span>
                  </div>
                  <div className={styles.sumRow}>
                    <span className={styles.sumLabel}>Client Amount (Gross)</span>
                    <span className={styles.sumValue}>&euro; {moneyFmt(grossNum)}</span>
                  </div>
                  <div className={styles.sumRow}>
                    <span className={styles.sumLabel}>Transaction Cost</span>
                    <span className={feeNum > 0 ? styles.sumValueNeg : styles.sumValue}>{feeNum > 0 ? "- " : ""}&euro; {moneyFmt(feeNum)}</span>
                  </div>
                  <div className={styles.sumDivider} />
                  <div className={styles.sumRow}>
                    <span className={styles.sumLabelBold}>Net Received</span>
                    <span className={styles.sumValuePos}>&euro; {moneyFmt(netReceived)}</span>
                  </div>
                </div>
              )}

              {/* Refund Summary — outgoing payments only */}
              {hasAmount && isOutgoing && (
                <div className={styles.sumCard}>
                  <div className={styles.sumCardHeader}>
                    <FileText size={15} />
                    <span>Refund Summary</span>
                  </div>
                  <div className={styles.sumRow}>
                    <span className={styles.sumLabelBold}>Amount Paid to Client</span>
                    <span className={styles.sumValueNeg}>&euro; {moneyFmt(grossNum)}</span>
                  </div>
                </div>
              )}

              {/* Booking Summary — only once an amount is entered */}
              {hasAmount && (
                <div className={styles.sumCard}>
                  <div className={styles.sumCardHeader}>
                    <Calendar size={15} />
                    <span>Booking Summary</span>
                  </div>
                  <div className={styles.sumRow}>
                    <span className={styles.sumLabel}>Booking Total</span>
                    <span className={styles.sumValue}>&euro; {moneyFmt(bookingTot)}</span>
                  </div>
                  <div className={styles.sumRow}>
                    <span className={styles.sumLabel}>Net Paid<br /><small className={styles.sumLabelSub}>{isOutgoing ? "(After this payout)" : "(Incl. this payment, after cost)"}</small></span>
                    <span className={paidAfter > 0 ? styles.sumValuePos : styles.sumValue}>&euro; {moneyFmt(paidAfter)}</span>
                  </div>
                  <div className={styles.sumRow}>
                    <span className={styles.sumLabel}>Applied to Booking</span>
                    <span className={styles.sumValue}>&euro; {moneyFmt(appliedAfter)}</span>
                  </div>
                  {creditAfter > 0.005 && (
                    <div className={styles.sumRow}>
                      <span className={styles.sumLabel}>Customer Credit</span>
                      <span className={styles.sumValueCredit}>&euro; {moneyFmt(creditAfter)}</span>
                    </div>
                  )}
                  <div className={styles.sumDivider} />
                  <div className={styles.sumRow}>
                    <span className={styles.sumLabelBold}>Outstanding</span>
                    <span className={outstandingAfter > 0 ? styles.sumValueNeg : styles.sumValuePos}>&euro; {moneyFmt(outstandingAfter)}</span>
                  </div>
                </div>
              )}

              {/* How It Works — incoming only (explains fee/net) */}
              {!isOutgoing && (
              <div className={styles.sumCard}>
                <div className={styles.sumCardHeader}>
                  <Info size={15} />
                  <span>How It Works</span>
                </div>
                <p className={styles.howItWorksText}>
                  <strong>Client Amount</strong> is the total amount paid by the client.
                </p>
                <p className={styles.howItWorksText}>
                  <strong>Transaction Cost</strong> is the cost charged by the payment provider / terminal.
                </p>
                <p className={styles.howItWorksText}>
                  <strong>Net Received</strong> is the amount actually received on your bank account (gross − transaction cost) — it is what settles the booking.
                </p>
                <p className={styles.howItWorksText}>
                  When <strong>“Client is covering the transaction cost”</strong> is ticked, the cost is billed to the client — it is added to what they owe, so the <strong>Outstanding</strong> rises by the cost. Untick it to let SUNSKY absorb the cost instead.
                </p>
                <p className={styles.howItWorksText}>
                  Any amount paid above the booking value becomes <strong>Customer Credit</strong>; outstanding can never go negative.
                </p>
              </div>
              )}
            </div>
          </div>

          {/* Removed-fields notice (full width, below both columns) */}
          <div className={styles.removedBanner}>
            <AlertCircle size={18} className={styles.removedBannerIcon} />
            <div>
              <div className={styles.removedBannerTitle}>Fields removed from client payment (not needed)</div>
              <div className={styles.removedBannerItem}>Airline PNR</div>
              <div className={styles.removedBannerItem}>Supplier Reference No</div>
              <div className={styles.removedBannerNote}>These fields are related to supplier and product data, not client payments.</div>
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.btnCancel} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <LoadingButton
            className={styles.btnSaveBlue}
            loading={saving}
            loadingText={editData ? "Updating…" : "Saving…"}
            onClick={validateAndSubmit}
          >
            {editData ? "Update Payment" : "Save Payment"}
          </LoadingButton>
        </div>
      </div>
    </div>
  );
};

/* ===================================================================
   AddSupplierPaymentModal
   =================================================================== */

/**
 * Build a supplier list from real booking components.
 * Groups components by supplierName; each product's `due` = its costPrice
 * (the maximum that can be paid to the supplier for that product).
 */
const buildSuppliersFromComponents = (bookingComponents) => {
  const groups = {};
  (bookingComponents || []).forEach((c) => {
    const supplierName = (c.supplierName || "").trim() || "Unknown Supplier";
    if (!groups[supplierName]) {
      groups[supplierName] = { id: supplierName, name: supplierName, products: [] };
    }
    const pnrField = (c.fields || []).find(
      (f) => f.label === "PNR" || f.label === "Supplier Ref"
    );
    const pnr = pnrField?.value && pnrField.value !== "—" ? pnrField.value : (c._raw?.supplierReference || "—");
    const pnrLabel =
      c.type === "Flight" ? "PNR" :
        c.type === "Hotel" ? "Res" :
          c.type === "Transfer" ? "Ref" : "Ref";

    const nameField = (c.fields || []).find(
      (f) => f.label === "Hotel Name" || f.label === "Transfer Type" || f.label === "From"
    );
    const productName = nameField?.value && nameField.value !== "—"
      ? nameField.value
      : (supplierName || c.type);

    // Extract flight segment details for outbound/inbound display
    let flightSegments = null;
    if (c.type === "Flight" && c._raw?.productDetails) {
      const d = typeof c._raw.productDetails === "string" ? JSON.parse(c._raw.productDetails) : c._raw.productDetails;
      const legs = d.legs || [];
      const outLegs = legs.filter((l) => l.direction === "Outbound");
      const inLegs = legs.filter((l) => l.direction === "Inbound");
      const isSplit = d.flightPricingMode === "SPLIT";
      const flightType = d.flightType || "OW";

      if ((flightType === "RT" || flightType === "Multi") && (outLegs.length > 0 || inLegs.length > 0)) {
        const buildSegment = (segLegs, dir) => {
          if (segLegs.length === 0) return null;
          const first = segLegs[0];
          const last = segLegs[segLegs.length - 1];
          return {
            direction: dir,
            airline: first.airlineName || first.airline || "",
            airlineCode: first.airlineCode || "",
            route: `${(first.departureAirport || "").toUpperCase()} → ${(last.arrivalAirport || "").toUpperCase()}`,
            date: first.departureDate || "",
            pnr: isSplit ? ((dir === "Outbound" ? d.outboundPnr : d.inboundPnr) || d.airlinePnr || "") : d.airlinePnr || pnr,
          };
        };
        flightSegments = {
          isSplit,
          flightType,
          outbound: buildSegment(outLegs, "Outbound"),
          inbound: buildSegment(inLegs, "Inbound"),
        };
      }
    }

    const supplierRefField = (c.fields || []).find((f) => f.label === "Supplier Ref");
    const supplierRef = supplierRefField?.value && supplierRefField.value !== "—"
      ? supplierRefField.value
      : (c._raw?.supplierReference || "");

    let outboundPnr = "";
    let inboundPnr = "";
    if (c.type === "Flight" && flightSegments?.isSplit) {
      outboundPnr = flightSegments.outbound?.pnr || "";
      inboundPnr = flightSegments.inbound?.pnr || "";
    }

    if (c.type === "Flight" && flightSegments?.isSplit && flightSegments.outbound && flightSegments.inbound) {
      const halfCost = (c.costPrice || 0) / 2;
      const segDefs = [
        { seg: flightSegments.outbound, dir: "Outbound", suffix: "-out", icon: "→" },
        { seg: flightSegments.inbound, dir: "Inbound", suffix: "-in", icon: "←" },
      ];
      segDefs.forEach(({ seg, dir, suffix }) => {
        groups[supplierName].products.push({
          id: `${c.id}${suffix}`,
          parentId: c.id,
          name: seg.route || productName,
          type: "Flight",
          pnr: seg.pnr || pnr,
          pnrLabel: "PNR",
          supplierRef: seg.pnr || supplierRef,
          outboundPnr: "",
          inboundPnr: "",
          due: halfCost,
          maxAmount: halfCost,
          flightSegments: null,
          splitDirection: dir,
          splitAirline: seg.airline || "",
          splitAirlineCode: seg.airlineCode || "",
          splitRoute: seg.route || "",
          splitDate: seg.date || "",
        });
      });
    } else {
      // When a product is cancelled, the relevant supplier figure is the refund we
      // expect back: supplier cost (net + tax, from pricing) − cancellation cost
      // (the supplier penalty persisted during the cancellation flow).
      const pd = typeof c._raw?.productDetails === "string"
        ? (() => { try { return JSON.parse(c._raw.productDetails); } catch { return {}; } })()
        : (c._raw?.productDetails || {});
      const isCancelled = c.status === "Cancelled" || c._raw?.productStatus === "Cancelled";
      const cancellationCost = Number(pd.cancellation?.cost) || 0;
      const supplierRefundDue = isCancelled ? Math.max((c.costPrice || 0) - cancellationCost, 0) : 0;
      groups[supplierName].products.push({
        id: c.id,
        name: productName,
        type: c.type,
        pnr,
        pnrLabel,
        supplierRef,
        outboundPnr,
        inboundPnr,
        due: c.costPrice || 0,
        maxAmount: c.costPrice || 0,
        flightSegments,
        isCancelled,
        cancellationCost,
        supplierRefundDue,
      });
    }
  });
  return Object.values(groups);
};

const AddSupplierPaymentModal = ({ open, onClose, onSave, bookingComponents = [], bookingTravelers = [], bookingAssignments = [], editData, saving = false, bookingRef = "" }) => {
  const realSuppliers = buildSuppliersFromComponents(bookingComponents);

  const [form, setForm] = useState({
    method: "",
    date: new Date().toISOString().slice(0, 10),
    currency: "EUR",
    reference: bookingRef,
    notes: "",
    supplierId: "",
    status: "Pending",
    paymentType: "Outgoing Payment",
    accountId: "",
    accountNumber: "",
    airlinePnr: "",
    supplierReferenceNo: "",
    transactionId: "",
    transactionFee: "",
    feePaidBy: "sunsky",
  });
  const [allSupplierAccounts, setAllSupplierAccounts] = useState([]);

  useEffect(() => {
    if (!open) return;
    getAccounts({ usage: "supplier", active: true, limit: 100 })
      .then((res) => setAllSupplierAccounts((res.data || []).map((a) => ({
        value: String(a.id),
        label: a.account_name,
        linkedPaymentMethods: a.linked_payment_methods || [],
        accountType: a.account_type,
        accountNumber: a.account_number || "",
      }))))
      .catch(() => setAllSupplierAccounts([]));
  }, [open]);

  const filteredSupplierAccounts = allSupplierAccounts.filter((acc) => {
    if (!form.method) return true;
    const apiKey = METHOD_TO_API_KEY[form.method];
    if (!apiKey) return true;
    return (acc.linkedPaymentMethods || []).includes(apiKey);
  });

  const [selected, setSelected] = useState({});
  const [payAmounts, setPayAmounts] = useState({});
  const [amountErrors, setAmountErrors] = useState({});
  const [travelerAmounts, setTravelerAmounts] = useState({});
  const [expandedFlights, setExpandedFlights] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (open && editData) {
      const matchedSupplier = realSuppliers.find(
        (s) => s.name.toLowerCase() === (editData.supplier || editData.supplierName || "").toLowerCase()
      );
      setForm({
        method: editData.method || "",
        date: editData.date || new Date().toISOString().slice(0, 10),
        currency: editData.currency || "EUR",
        reference: editData.reference || "",
        notes: editData.notes || "",
        supplierId: matchedSupplier ? matchedSupplier.id : "",
        status: editData.status || "Pending",
        paymentType: editData.paymentTypeDisplay || toDisplayPaymentType(editData.paymentType) || "Outgoing Payment",
        accountId: editData.accountId ? String(editData.accountId) : "",
        accountNumber: editData.accountNumber || "",
        airlinePnr: editData.airlinePnr || "",
        supplierReferenceNo: editData.supplierReferenceNo || "",
        transactionId: editData.transactionId || "",
        transactionFee: editData.transactionFee != null ? String(editData.transactionFee) : "",
        feePaidBy: editData.feePaidBy || "sunsky",
      });
      if (matchedSupplier) {
        const sel = {};
        const amounts = {};
        matchedSupplier.products.forEach((p) => {
          sel[p.id] = true;
          amounts[p.id] = String(p.due);
        });
        setSelected(sel);
        setPayAmounts(amounts);
      }
      setAmountErrors({});
      setTravelerAmounts({});
      setExpandedFlights({});
    } else if (open && !editData) {
      setForm({
        method: "",
        date: new Date().toISOString().slice(0, 10),
        currency: "EUR",
        reference: bookingRef,
        notes: "",
        supplierId: "",
        status: "Pending",
        paymentType: "Outgoing Payment",
        accountId: "",
        accountNumber: "",
        airlinePnr: "",
        supplierReferenceNo: "",
        transactionId: "",
        transactionFee: "",
        feePaidBy: "sunsky",
      });
      setSelected({});
      setPayAmounts({});
      setAmountErrors({});
      setTravelerAmounts({});
      setExpandedFlights({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editData]);

  if (!open) return null;

  const set = (k, v) => {
    setForm((p) => {
      const next = { ...p, [k]: v };
      if (k === "method") { next.accountId = ""; next.accountNumber = ""; }
      if (k === "accountId") {
        const selectedAcc = filteredSupplierAccounts.find((a) => a.value === v);
        next.accountNumber = selectedAcc?.accountNumber || "";
      }
      return next;
    });
    if (fieldErrors[k]) setFieldErrors((p) => { const n = { ...p }; delete n[k]; return n; });
  };
  const notesLen = (form.notes || "").length;
  const showSupplierAccountNumber = form.method === "Bank Transfer" || form.method === "SEPA" || form.method === "SWIFT";
  const supplierAccountHasNumber = !!form.accountId && !!form.accountNumber;

  const validateAndSubmit = () => {
    const errs = {};
    if (!form.method) errs.method = "Payment method is required";
    if (!form.accountId) errs.accountId = "Account is required";
    if (!form.supplierId) errs.supplierId = "Supplier is required";
    if (Object.keys(selected).length === 0) errs.products = "Select at least one product";
    const selectedProducts = products.filter((p) => selected[p.id]);
    const totalAmount = selectedProducts.reduce((s, p) => s + getProductPayAmount(p), 0);
    // SUNSKY supplier fee logic: when the SUPPLIER bears the fee it is deducted
    // from what they receive — it can never exceed the supplier amount itself.
    if (isSupplierOutgoingType && supFeeNum > 0 && form.feePaidBy === "supplier" && supFeeNum > totalAmount && totalAmount > 0) {
      errs.transactionFee = "Transaction cost cannot exceed the supplier amount when the supplier bears the cost";
    }
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }
    if (hasErrors) return;
    onSave && onSave({
      ...form,
      supplierName: activeSupplier?.name || "",
      component: selectedProducts.map((p) => p.type).join(", "),
      totalAmount,
      transactionFee: isSupplierOutgoingType ? supFeeNum : 0,
      feePaidBy: isSupplierOutgoingType && supFeeNum > 0 ? form.feePaidBy : null,
      selectedProducts,
      selected,
      payAmounts,
      travelerAmounts,
      expandedFlights,
    });
  };

  const activeSupplier = realSuppliers.find((s) => s.id === form.supplierId);
  const products = activeSupplier?.products || [];

  const updateReferenceFromSelected = (selectedMap) => {
    const selectedProductIds = Object.keys(selectedMap).filter((k) => selectedMap[k]);
    if (selectedProductIds.length === 0) {
      set("reference", "");
      return;
    }
    const selectedProds = products.filter((p) => selectedMap[p.id]);
    const refs = [];
    selectedProds.forEach((p) => {
      if (p.type === "Flight" && p.flightSegments?.isSplit) {
        if (p.outboundPnr) refs.push(`Outbound PNR: ${p.outboundPnr}`);
        if (p.inboundPnr) refs.push(`Inbound PNR: ${p.inboundPnr}`);
        if (!p.outboundPnr && !p.inboundPnr && p.supplierRef) refs.push(p.supplierRef);
      } else {
        const ref = p.supplierRef || p.pnr;
        if (!ref || ref === "—") return;
        if (selectedProds.length > 1) {
          const label = p.type === "Flight" ? "PNR" : p.type === "Hotel" ? "Res" : "Ref";
          refs.push(`${p.name} (${label}: ${ref})`);
        } else {
          refs.push(ref);
        }
      }
    });
    set("reference", [...new Set(refs)].join(", "));
  };

  const toggleProduct = (pid) => {
    const product = products.find((pr) => pr.id === pid);
    setSelected((p) => {
      const next = { ...p };
      if (next[pid]) {
        delete next[pid];
        setPayAmounts((prev) => { const n = { ...prev }; delete n[pid]; return n; });
      } else {
        next[pid] = true;
        if (product && product.due > 0) {
          setPayAmounts((prev) => ({ ...prev, [pid]: String(product.due.toFixed(2)) }));
        }
      }
      updateReferenceFromSelected(next);
      return next;
    });
  };

  const handleAmountChange = (pid, value, maxAmount) => {
    setPayAmounts((prev) => ({ ...prev, [pid]: value }));
    const num = Math.round((parseFloat(value) || 0) * 100);
    const max = Math.round((maxAmount || 0) * 100);
    if (num > max) {
      setAmountErrors((prev) => ({ ...prev, [pid]: `Max payable: € ${maxAmount.toFixed(2)}` }));
    } else {
      setAmountErrors((prev) => { const next = { ...prev }; delete next[pid]; return next; });
    }
  };

  const getTravelersForProduct = (productId) => {
    const lookupId = products.find((p) => p.id === productId)?.parentId || productId;
    const assigned = (bookingAssignments || []).filter((a) => a.productId === lookupId);
    return assigned
      .map((a) => {
        const t = (bookingTravelers || []).find((tr) => tr.id === a.travelerId);
        return t ? { ...t, assignmentId: `${productId}-${t.id}` } : null;
      })
      .filter(Boolean);
  };

  const handleTravelerAmountChange = (key, value, maxPerTraveler) => {
    setTravelerAmounts((prev) => ({ ...prev, [key]: value }));
    const num = parseFloat(value) || 0;
    if (maxPerTraveler && num > maxPerTraveler) {
      setAmountErrors((prev) => ({ ...prev, [key]: `Max: € ${maxPerTraveler.toFixed(2)}` }));
    } else {
      setAmountErrors((prev) => { const next = { ...prev }; delete next[key]; return next; });
    }
  };

  const toggleFlightExpand = (pid) => {
    setExpandedFlights((prev) => ({ ...prev, [pid]: !prev[pid] }));
  };

  const getProductPayAmount = (p) => {
    if (p.type === "Flight" && expandedFlights[p.id]) {
      const travelers = getTravelersForProduct(p.id);
      const segs = p.flightSegments;
      if (segs && (segs.outbound || segs.inbound)) {
        let sum = 0;
        travelers.forEach((t) => {
          if (segs.outbound) sum += parseFloat(travelerAmounts[`${p.id}-out-${t.id}`] || 0);
          if (segs.inbound) sum += parseFloat(travelerAmounts[`${p.id}-in-${t.id}`] || 0);
        });
        return sum;
      }
      return travelers.reduce((s, t) => s + (parseFloat(travelerAmounts[`${p.id}-${t.id}`] || 0)), 0);
    }
    return parseFloat(payAmounts[p.id] || 0);
  };

  const totalPayable = products.reduce((s, p) => s + (p.due || 0), 0);
  const totalPayment = products
    .filter((p) => selected[p.id])
    .reduce((s, p) => s + getProductPayAmount(p), 0);

  const hasErrors = Object.keys(amountErrors).length > 0;

  // ── SUNSKY supplier payment fee logic (live preview) ──
  //   Sunsky pays fee  → bank outgoing = amount + fee, supplier receives full amount
  //   Supplier pays fee→ bank outgoing = amount, supplier receives amount − fee
  const isSupplierOutgoingType = form.paymentType === "Outgoing Payment";
  const supFeeNum = isSupplierOutgoingType
    ? (parseFloat(String(form.transactionFee).replace(",", ".")) || 0)
    : 0;
  const supBankOutgoing = form.feePaidBy === "supplier" ? totalPayment : totalPayment + supFeeNum;
  const supNetToSupplier = form.feePaidBy === "supplier" ? Math.max(0, totalPayment - supFeeNum) : totalPayment;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={`${styles.modal} ${styles.modalWide}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <div>
            <h3 className={styles.modalTitle}>{editData ? "Edit Supplier Payment" : "Add Supplier Payment"}</h3>
            <p className={styles.modalSubtitle}>
              {editData ? "Update the details of this supplier payment." : "Record a payment related to supplier(s). You can pay for one or multiple products in this booking."}
            </p>
          </div>
          <button className={styles.modalClose} onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.modalSectionLabel}>1. Payment Details</div>

          {/* Payment Type & Status Row */}
          <div className={styles.formRow2}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                Payment Type <span className={styles.req}>*</span>
              </label>
              <div className={styles.selectWithIcon}>
                <span className={styles.typeDot} style={{ background: form.paymentType === "Outgoing Payment" ? "#eef3ff" : form.paymentType === "Refund" ? "#fef3e2" : "#e8f5ee", color: form.paymentType === "Outgoing Payment" ? "#2563eb" : form.paymentType === "Refund" ? "#b06e0a" : "#0d7a4f" }}>
                  {form.paymentType === "Outgoing Payment" ? "↑" : form.paymentType === "Refund" ? "↩" : "↓"}
                </span>
                <select
                  className={styles.formSelect}
                  value={form.paymentType}
                  onChange={(e) => set("paymentType", e.target.value)}
                  disabled={!!editData}
                >
                  {SUPPLIER_PAYMENT_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              {editData && <span className={styles.formHint}>Payment type cannot be changed after creation</span>}
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                Payment Status <span className={styles.req}>*</span>
              </label>
              <select
                className={styles.formSelect}
                value={form.status}
                onChange={(e) => set("status", e.target.value)}
              >
                {PAYMENT_STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.formRow3}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                Payment Method <span className={styles.req}>*</span>
              </label>
              <div className={styles.selectWithIcon}>
                <span
                  className={styles.methodDot}
                  style={{
                    background: getMethodIcon(form.method).bg,
                    color: getMethodIcon(form.method).color,
                  }}
                >
                  {getMethodIcon(form.method).icon}
                </span>
                <select
                  className={styles.formSelect}
                  style={fieldErrors.method ? { border: "1.5px solid #ef4444" } : undefined}
                  value={form.method}
                  onChange={(e) => set("method", e.target.value)}
                >
                  <option value="">Select payment method...</option>
                  {SUPPLIER_PAYMENT_METHODS.map((m) => (
                    <option key={m.label} value={m.label}>{m.label}</option>
                  ))}
                </select>
              </div>
              {fieldErrors.method && <div className={styles.fieldError}>{fieldErrors.method}</div>}
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                Payment Date <span className={styles.req}>*</span>
              </label>
              <input
                type="date"
                className={styles.formInput}
                value={form.date}
                onChange={(e) => set("date", e.target.value)}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                Currency <span className={styles.req}>*</span>
              </label>
              <select
                className={styles.formSelect}
                value={form.currency}
                onChange={(e) => set("currency", e.target.value)}
              >
                <option value="EUR">EUR - Euro</option>
                <option value="USD">USD - US Dollar</option>
              </select>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              Account <span className={styles.req}>*</span>
            </label>
            <select
              className={styles.formSelect}
              style={fieldErrors.accountId ? { border: "1.5px solid #ef4444" } : undefined}
              value={form.accountId}
              onChange={(e) => set("accountId", e.target.value)}
            >
              <option value="">{form.method ? `Select ${form.method} account...` : "Select payment method first..."}</option>
              {filteredSupplierAccounts.map((acc) => (
                <option key={acc.value} value={acc.value}>{acc.label}</option>
              ))}
            </select>
            {fieldErrors.accountId && <div className={styles.fieldError}>{fieldErrors.accountId}</div>}
            {form.method && filteredSupplierAccounts.length === 0 && (
              <span className={styles.formHint} style={{ color: "#b06e0a" }}>No accounts linked to {form.method}. Add one in Finance → Accounts.</span>
            )}
          </div>

          {showSupplierAccountNumber && (
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                Account Number / IBAN
              </label>
              <input
                type="text"
                className={`${styles.formInput}${supplierAccountHasNumber ? ` ${styles.inputReadonly}` : ""}`}
                placeholder={supplierAccountHasNumber ? "" : "Select an account to auto-fill"}
                value={form.accountNumber}
                onChange={(e) => { if (!supplierAccountHasNumber) set("accountNumber", e.target.value); }}
                readOnly={supplierAccountHasNumber}
              />
              <span className={styles.formHint}>{supplierAccountHasNumber ? "Auto-filled from the selected account" : "Select an account above to auto-fill"}</span>
            </div>
          )}

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Airline PNR</label>
              <input
                className={styles.formInput}
                placeholder="e.g. ABC123"
                value={form.airlinePnr}
                onChange={(e) => set("airlinePnr", e.target.value)}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Supplier Reference No</label>
              <input
                className={styles.formInput}
                placeholder="e.g. SUP-REF-001"
                value={form.supplierReferenceNo}
                onChange={(e) => set("supplierReferenceNo", e.target.value)}
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Transaction ID</label>
            <input
              className={styles.formInput}
              placeholder="e.g. TXN-20240310-001"
              value={form.transactionId}
              onChange={(e) => set("transactionId", e.target.value)}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Notes</label>
            <textarea
              className={styles.formTextarea}
              rows={2}
              maxLength={255}
              placeholder="Payment for selected products."
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
            />
            <span className={styles.formCharCount}>{notesLen} / 255</span>
          </div>

          <div className={styles.modalDivider} />
          <div className={styles.modalSectionLabel}>
            2. Select Supplier &amp; Products to Pay
          </div>

          {realSuppliers.length === 0 ? (
            <div className={styles.infoBanner}>
              <Info size={14} />
              <span>No supplier products found for this booking. Add a product with a supplier first.</span>
            </div>
          ) : (
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                Supplier <span className={styles.req}>*</span>
              </label>
              <select
                className={styles.formSelect}
                style={fieldErrors.supplierId ? { border: "1.5px solid #ef4444" } : undefined}
                value={form.supplierId}
                onChange={(e) => {
                  const suppId = e.target.value;
                  set("supplierId", suppId);
                  setSelected({});
                  setPayAmounts({});
                  setAmountErrors({});
                  setTravelerAmounts({});
                  setExpandedFlights({});
                  set("reference", "");
                }}
              >
                <option value="">Select a supplier...</option>
                {realSuppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              {fieldErrors.supplierId && <div className={styles.fieldError}>{fieldErrors.supplierId}</div>}
            </div>
          )}

          {activeSupplier && products.length > 1 && (
            <div className={styles.infoBanner}>
              <Info size={14} />
              <span>
                This supplier has {products.length} product{products.length !== 1 ? "s" : ""} in this booking. You
                can pay for one or {products.length > 2 ? "more" : "both"}.
              </span>
            </div>
          )}

          {activeSupplier && (
            <div className={styles.formGroup} style={{ marginBottom: 12 }}>
              <label className={styles.formLabel}>
                Reference / Transaction ID
                <Info size={13} style={{ marginLeft: 4, verticalAlign: "middle", opacity: 0.5 }} />
              </label>
              <input
                type="text"
                className={styles.formInput}
                placeholder={Object.keys(selected).length > 0 ? "Auto-populated from selected product(s)" : "Select product(s) below to auto-populate"}
                value={form.reference}
                onChange={(e) => set("reference", e.target.value)}
              />
              <span className={styles.formHint}>
                {Object.keys(selected).length > 0
                  ? "Auto-populated from selected product(s). You can edit manually."
                  : "Select product(s) below to auto-populate the supplier reference."}
              </span>
            </div>
          )}

          {activeSupplier && (
            <div className={styles.productsTable}>
              <div className={styles.ptHeader}>
                <span className={styles.ptCb} />
                <span>Product</span>
                <span>Type</span>
                <span>PNR / Reference</span>
                <span className={styles.ptRight}>Due Amount</span>
                <span className={styles.ptRight}>Pay Amount</span>
              </div>
              {products.map((p) => {
                const isFlight = p.type === "Flight";
                const isExpanded = isFlight && expandedFlights[p.id];
                const flightTravelers = isFlight ? getTravelersForProduct(p.id) : [];
                const hasTravelers = flightTravelers.length > 0;
                const travelerSum = isExpanded
                  ? (() => {
                    const segs = p.flightSegments;
                    if (segs && (segs.outbound || segs.inbound)) {
                      return flightTravelers.reduce((s, t) => {
                        let v = 0;
                        if (segs.outbound) v += parseFloat(travelerAmounts[`${p.id}-out-${t.id}`] || 0);
                        if (segs.inbound) v += parseFloat(travelerAmounts[`${p.id}-in-${t.id}`] || 0);
                        return s + v;
                      }, 0);
                    }
                    return flightTravelers.reduce((s, t) => s + (parseFloat(travelerAmounts[`${p.id}-${t.id}`]) || 0), 0);
                  })()
                  : null;

                return (
                  <Fragment key={p.id}>
                    {/* ── Product Row ── */}
                    <div
                      className={`${styles.ptRow} ${selected[p.id] ? styles.ptRowSelected : ""
                        } ${isExpanded ? styles.ptRowExpanded : ""}`}
                    >
                      <span className={styles.ptCb}>
                        <label className={styles.checkbox}>
                          <input
                            type="checkbox"
                            checked={!!selected[p.id]}
                            onChange={() => toggleProduct(p.id)}
                          />
                          <span className={styles.checkmark}>
                            <Check size={12} />
                          </span>
                        </label>
                      </span>
                      <span className={styles.ptProduct}>
                        <span className={`${styles.ptProductIcon} ${isFlight ? (p.splitDirection === "Outbound" ? styles.ptProductIconFlight : p.splitDirection === "Inbound" ? styles.ptProductIconFlightIn : styles.ptProductIconFlight) : p.type === "Hotel" ? styles.ptProductIconHotel : p.type === "Transfer" ? styles.ptProductIconTransfer : styles.ptProductIconOther}`}>
                          {isFlight && <Plane size={13} style={p.splitDirection === "Inbound" ? { transform: "scaleX(-1)" } : undefined} />}
                          {p.type === "Hotel" && <Hotel size={13} />}
                          {p.type === "Transfer" && <Bus size={13} />}
                          {p.type === "Insurance" && <Shield size={13} />}
                        </span>
                        <span className={styles.ptProductInfo}>
                          <span className={styles.ptProductName}>{p.splitDirection ? `${p.splitRoute}` : p.name}</span>
                          {p.splitDirection && (
                            <span className={styles.ptFlightMeta}>
                              <span className={styles.ptSplitDirBadge} style={{ background: p.splitDirection === "Outbound" ? "#eef9f3" : "#eef3ff", color: p.splitDirection === "Outbound" ? "#0d7a4f" : "#2563eb", borderColor: p.splitDirection === "Outbound" ? "#c3e8d4" : "#c7d8f7" }}>{p.splitDirection}</span>
                              {p.splitAirlineCode && <span className={styles.ptSplitAirline}>{p.splitAirlineCode}</span>}
                              {p.splitDate && <span className={styles.ptSplitDate}>{p.splitDate}</span>}
                            </span>
                          )}
                          {isFlight && !p.splitDirection && p.flightSegments && (
                            <span className={styles.ptFlightMeta}>
                              {p.flightSegments.flightType === "RT" ? "Round Trip" : p.flightSegments.flightType === "Multi" ? "Multi-City" : ""}
                              {p.flightSegments.isSplit && <span className={styles.ptSplitBadge}>Split</span>}
                            </span>
                          )}
                          {isFlight && !p.splitDirection && hasTravelers && selected[p.id] && (
                            <button
                              type="button"
                              className={`${styles.expandTravelersBtn} ${isExpanded ? styles.expandTravelersBtnActive : ""}`}
                              onClick={() => toggleFlightExpand(p.id)}
                              title={isExpanded ? "Collapse per-traveller view" : "Pay per traveller"}
                            >
                              <Users size={11} />
                              <span>{flightTravelers.length} traveller{flightTravelers.length !== 1 ? "s" : ""}</span>
                              {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                            </button>
                          )}
                        </span>
                      </span>
                      <span className={styles.ptType}>
                        <span className={`${styles.ptTypeBadge} ${styles[`ptType${p.type}`] || ""}`}>{p.splitDirection ? `Flight · ${p.splitDirection}` : p.type}</span>
                      </span>
                      <span className={styles.ptPnr}>
                        <span className={styles.pnrBadge}>{p.pnrLabel}</span>
                        <span className={styles.pnrCode}>{p.pnr}</span>
                      </span>
                      <span className={styles.ptRight}>
                        <span className={styles.dueAmount}>{fmt(p.due)}</span>
                        {p.isCancelled && (
                          <span
                            style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#0d7a4f", marginTop: 2 }}
                            title={`Supplier cost ${fmt(p.due)} − cancellation cost ${fmt(p.cancellationCost)}`}
                          >
                            ↩ Refund due: {fmt(p.supplierRefundDue)}
                          </span>
                        )}
                      </span>
                      <span className={styles.ptRight}>
                        {isExpanded ? (
                          <div className={styles.travelerSumDisplay}>
                            <span className={styles.travelerSumLabel}>from travellers</span>
                            <span className={styles.travelerSumValue}>{fmt(travelerSum)}</span>
                          </div>
                        ) : (
                          <>
                            <div className={styles.payInputRow}>
                              <div className={styles.payInput}>
                                <span className={styles.payPrefix}>&euro;</span>
                                <input
                                  type="text"
                                  value={payAmounts[p.id] || ""}
                                  placeholder="0,00"
                                  style={amountErrors[p.id] ? { border: "1.5px solid #ef4444" } : undefined}
                                  onChange={(e) => handleAmountChange(p.id, e.target.value, p.maxAmount)}
                                  disabled={!selected[p.id]}
                                />
                              </div>
                              {selected[p.id] && p.maxAmount > 0 && (
                                <button
                                  type="button"
                                  className={styles.fillDueBtn}
                                  title="Fill with due amount"
                                  onClick={() => handleAmountChange(p.id, p.maxAmount.toFixed(2), p.maxAmount)}
                                >
                                  Max
                                </button>
                              )}
                            </div>
                            {amountErrors[p.id] && (
                              <div className={styles.fieldError}>
                                {amountErrors[p.id]}
                              </div>
                            )}
                          </>
                        )}
                      </span>
                    </div>

                    {/* ── Per-traveller breakdown for flights ── */}
                    {isExpanded && (() => {
                      const segs = p.flightSegments;
                      const hasSegments = segs && (segs.outbound || segs.inbound);

                      const renderTravelerRow = (t, idx, keyPrefix, maxPer, total) => {
                        const key = keyPrefix;
                        const initials = (t.name || "").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
                        return (
                          <div key={key} className={`${styles.travelerSubRow} ${idx === total - 1 ? styles.travelerSubRowLast : ""}`}>
                            <span className={`${styles.travelerAvatar} ${t.type === "Child" ? styles.travelerAvatarChild :
                                t.type === "Infant" ? styles.travelerAvatarInfant :
                                  styles.travelerAvatarAdult
                              }`}>{initials}</span>
                            <span className={styles.travelerNameCol}>
                              <span className={styles.travelerName}>{t.name}</span>
                              {t.dob && <span className={styles.travelerDob}>{t.dob}</span>}
                            </span>
                            <span>
                              <span className={`${styles.travelerTypeBadge} ${t.type === "Child" ? styles.travelerTypeChild :
                                  t.type === "Infant" ? styles.travelerTypeInfant :
                                    styles.travelerTypeAdult
                                }`}>{t.type || "Adult"}</span>
                            </span>
                            <span className={styles.ptRight}>
                              <span className={styles.perPersonAmount}>{fmt(maxPer)}</span>
                            </span>
                            <span className={styles.ptRight}>
                              <div className={styles.travelerPayWrap}>
                                <div className={`${styles.payInput} ${styles.payInputTraveler}`}>
                                  <span className={styles.payPrefix}>&euro;</span>
                                  <input
                                    type="text"
                                    value={travelerAmounts[key] || ""}
                                    placeholder="0,00"
                                    style={amountErrors[key] ? { border: "1.5px solid #ef4444" } : undefined}
                                    onChange={(e) => handleTravelerAmountChange(key, e.target.value, maxPer)}
                                  />
                                </div>
                                {flightTravelers.length > 1 && travelerAmounts[key] && (
                                  <button type="button" className={styles.copyToAllBtn} title="Copy to all travellers in this segment"
                                    onClick={() => {
                                      const val = travelerAmounts[key];
                                      const newAmounts = { ...travelerAmounts };
                                      const newErrors = { ...amountErrors };
                                      flightTravelers.forEach((tr) => {
                                        const k = keyPrefix.replace(/-\d+$/, `-${tr.id}`);
                                        newAmounts[k] = val;
                                        const num = parseFloat(val) || 0;
                                        if (maxPer && num > maxPer) newErrors[k] = `Max: € ${maxPer.toFixed(2)}`;
                                        else delete newErrors[k];
                                      });
                                      setTravelerAmounts(newAmounts);
                                      setAmountErrors(newErrors);
                                    }}
                                  ><Copy size={11} /> All</button>
                                )}
                              </div>
                              {amountErrors[key] && <div className={styles.fieldError}>{amountErrors[key]}</div>}
                            </span>
                          </div>
                        );
                      };

                      if (hasSegments) {
                        const segmentBlocks = [];
                        const segDefs = [
                          { seg: segs.outbound, dir: "Outbound", prefix: "out", color: "#0d7a4f", bg: "#eef9f3", border: "#c3e8d4" },
                          { seg: segs.inbound, dir: "Inbound", prefix: "in", color: "#2563eb", bg: "#eef3ff", border: "#c7d8f7" },
                        ];
                        let grandTotal = 0;
                        segDefs.forEach(({ seg, dir, prefix, color, bg, border }) => {
                          if (!seg) return;
                          const maxPerSeg = p.maxAmount && flightTravelers.length
                            ? (p.maxAmount / 2) / flightTravelers.length : 0;
                          const segSum = flightTravelers.reduce((s, t) =>
                            s + (parseFloat(travelerAmounts[`${p.id}-${prefix}-${t.id}`] || 0)), 0);
                          grandTotal += segSum;
                          segmentBlocks.push(
                            <div key={dir} className={styles.segmentBlock}>
                              <div className={styles.segmentHeader} style={{ background: bg, borderColor: border }}>
                                <div className={styles.segmentHeaderLeft}>
                                  <Plane size={13} style={{ color, transform: dir === "Inbound" ? "scaleX(-1)" : "none" }} />
                                  <div>
                                    <div className={styles.segmentDir} style={{ color }}>{dir}</div>
                                    <div className={styles.segmentRoute}>{seg.route}</div>
                                  </div>
                                </div>
                                <div className={styles.segmentHeaderRight}>
                                  {seg.airline && <span className={styles.segmentAirline}>{seg.airlineCode || seg.airline}</span>}
                                  <span className={styles.segmentPnr}>PNR: {seg.pnr || "—"}</span>
                                </div>
                              </div>
                              <div className={styles.travelerSubHeader}>
                                <span />
                                <span>Traveller</span>
                                <span>Type</span>
                                <span className={styles.ptRight}>Share</span>
                                <span className={styles.ptRight}>Pay Amount</span>
                              </div>
                              {flightTravelers.map((t, idx) =>
                                renderTravelerRow(t, idx, `${p.id}-${prefix}-${t.id}`, maxPerSeg, flightTravelers.length)
                              )}
                              <div className={styles.travelerSubFooter}>
                                <button type="button" className={styles.fillEqualBtn}
                                  onClick={() => {
                                    const perPerson = maxPerSeg.toFixed(2);
                                    const newAmounts = { ...travelerAmounts };
                                    const newErrors = { ...amountErrors };
                                    flightTravelers.forEach((t) => {
                                      newAmounts[`${p.id}-${prefix}-${t.id}`] = perPerson;
                                      delete newErrors[`${p.id}-${prefix}-${t.id}`];
                                    });
                                    setTravelerAmounts(newAmounts);
                                    setAmountErrors(newErrors);
                                  }}
                                >
                                  <Split size={12} /> Split equally &mdash; {fmt(maxPerSeg)} / person
                                </button>
                                <div className={styles.travelerSubTotalWrap}>
                                  <span className={styles.travelerSubTotalLabel}>{dir} Total</span>
                                  <span className={styles.travelerSubTotalValue}>{fmt(segSum)}</span>
                                </div>
                              </div>
                            </div>
                          );
                        });

                        return (
                          <div className={styles.travelerSubSection}>
                            {segmentBlocks}
                            <div className={`${styles.travelerSubFooter} ${styles.travelerGrandFooter}`}>
                              <div className={styles.travelerSubTotalWrap}>
                                <span className={styles.travelerSubTotalLabel}>Grand Total</span>
                                <span className={`${styles.travelerSubTotalValue} ${grandTotal > 0 && Math.abs(grandTotal - p.due) < 0.01 ? styles.travelerSubTotalMatch : ""}`}>
                                  {fmt(grandTotal)}
                                  {grandTotal > 0 && Math.abs(grandTotal - p.due) < 0.01 && <Check size={13} />}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      // Fallback: simple per-traveler (one-way or no segment info)
                      const maxPerTraveler = p.maxAmount && flightTravelers.length
                        ? p.maxAmount / flightTravelers.length : 0;
                      return (
                        <div className={styles.travelerSubSection}>
                          <div className={styles.travelerSubHeader}>
                            <span />
                            <span>Traveller</span>
                            <span>Type</span>
                            <span className={styles.ptRight}>Share</span>
                            <span className={styles.ptRight}>Pay Amount</span>
                          </div>
                          {flightTravelers.map((t, idx) =>
                            renderTravelerRow(t, idx, `${p.id}-${t.id}`, maxPerTraveler, flightTravelers.length)
                          )}
                          <div className={styles.travelerSubFooter}>
                            <button type="button" className={styles.fillEqualBtn}
                              onClick={() => {
                                const perPerson = maxPerTraveler.toFixed(2);
                                const newAmounts = { ...travelerAmounts };
                                const newErrors = { ...amountErrors };
                                flightTravelers.forEach((t) => {
                                  newAmounts[`${p.id}-${t.id}`] = perPerson;
                                  delete newErrors[`${p.id}-${t.id}`];
                                });
                                setTravelerAmounts(newAmounts);
                                setAmountErrors(newErrors);
                              }}
                            >
                              <Split size={12} /> Split equally &mdash; {fmt(maxPerTraveler)} / person
                            </button>
                            <div className={styles.travelerSubTotalWrap}>
                              <span className={styles.travelerSubTotalLabel}>Traveller Total</span>
                              <span className={`${styles.travelerSubTotalValue} ${travelerSum > 0 && Math.abs(travelerSum - p.due) < 0.01 ? styles.travelerSubTotalMatch : ""}`}>
                                {fmt(travelerSum)}
                                {travelerSum > 0 && Math.abs(travelerSum - p.due) < 0.01 && <Check size={13} />}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </Fragment>
                );
              })}

              {/* ── Summary Footer ── */}
              <div className={styles.ptFooter}>
                <div className={styles.ptFooterLeft}>
                  <span className={styles.ptFooterLabel}>Total Due</span>
                  <span className={styles.ptFooterVal}>{fmt(totalPayable)}</span>
                </div>
                <div className={styles.ptFooterDivider} />
                <div className={styles.ptFooterRight2}>
                  <span className={styles.ptFooterLabel}>Payment Total</span>
                  <span className={`${styles.ptFooterValBig} ${totalPayment > 0 ? styles.ptFooterValActive : ""}`}>{fmt(totalPayment)}</span>
                </div>
              </div>
            </div>
          )}

          {/* ── Transaction fee & bearer (SUNSKY supplier fee logic) — outgoing only ── */}
          {isSupplierOutgoingType && (
            <>
              <div className={styles.modalDivider} />
              <div className={styles.modalSectionLabel}>3. Transaction Cost</div>
              <div className={styles.formRow2}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Transaction Cost</label>
                  <div className={styles.inputWithPrefix}>
                    <span className={styles.inputPrefix}>&euro;</span>
                    <input
                      type="text"
                      className={`${styles.formInput}${fieldErrors.transactionFee ? ` ${styles.inputError}` : ""}`}
                      placeholder="0,00"
                      value={form.transactionFee}
                      onChange={(e) => set("transactionFee", e.target.value)}
                    />
                  </div>
                  {fieldErrors.transactionFee && <div className={styles.fieldError}>{fieldErrors.transactionFee}</div>}
                  <span className={styles.formHint}>Cost charged by the payment provider for this outgoing payment.</span>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Cost Paid By {supFeeNum > 0 && <span className={styles.req}>*</span>}</label>
                  <select
                    className={styles.formSelect}
                    value={form.feePaidBy}
                    onChange={(e) => set("feePaidBy", e.target.value)}
                  >
                    <option value="sunsky">Sunsky — we pay the cost (recommended)</option>
                    <option value="supplier">Supplier — cost deducted from their amount</option>
                  </select>
                  <span className={styles.formHint}>
                    {form.feePaidBy === "supplier"
                      ? "Only when the supplier contract explicitly states the supplier bears the payment cost."
                      : "The cost is a Sunsky bank expense on top of the supplier amount — the supplier receives the full invoice amount."}
                  </span>
                </div>
              </div>
              {supFeeNum > 0 && totalPayment > 0 && (
                <div className={styles.feePreviewStrip}>
                  <span className={styles.feePreviewItem}>Supplier Amount <b>{fmt(totalPayment)}</b></span>
                  <span className={styles.feePreviewOp}>{form.feePaidBy === "supplier" ? "→" : "+"}</span>
                  <span className={styles.feePreviewItem}>Cost <b>{fmt(supFeeNum)}</b></span>
                  <span className={styles.feePreviewOp}>=</span>
                  <span className={styles.feePreviewItem}>Bank Outgoing <b>{fmt(supBankOutgoing)}</b></span>
                  <span className={styles.feePreviewOp}>·</span>
                  <span className={styles.feePreviewItem}>Supplier receives <b>{fmt(supNetToSupplier)}</b></span>
                </div>
              )}
            </>
          )}

          <div className={styles.modalDivider} />
          <div className={styles.modalSectionLabel}>
            {isSupplierOutgoingType ? "4." : "3."} Attachments (Optional)
          </div>
          <button className={styles.uploadBtn}>
            <Upload size={14} /> Upload File
          </button>
          <span className={styles.formHint}>
            Upload invoice, payment proof or remittance advice
          </span>
        </div>

        <div className={styles.modalFooter}>
          {fieldErrors.products && (
            <span className={styles.footerFieldError}>{fieldErrors.products}</span>
          )}
          <button className={styles.btnCancel} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <LoadingButton
            className={styles.btnSaveOrange}
            loading={saving}
            loadingText={editData ? "Updating…" : "Saving…"}
            disabled={hasErrors}
            onClick={validateAndSubmit}
          >
            {editData ? "Update Supplier Payment" : "Save Supplier Payment"}
          </LoadingButton>
        </div>
      </div>
    </div>
  );
};

/* ===================================================================
   ViewAllClientPaymentsModal
   =================================================================== */
const ViewAllClientPaymentsModal = ({ open, onClose, payments, totals, onDeletePayment, bookingRef = "", customerName = "" }) => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selectedRow, setSelectedRow] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [editingPayment, setEditingPayment] = useState(null);

  if (!open) return null;

  const countByStatus = (st) =>
    payments.filter((p) =>
      st === "Paid" ? p.status === "Paid" || p.status === "Completed" :
        st === "Pending" ? p.status === "Pending" :
          st === "Unpaid" ? p.status === "Unpaid" || p.status === "Not Paid" : true
    ).length;

  const filtered = payments.filter((p) => {
    const matchSearch =
      !search ||
      (p.method || "").toLowerCase().includes(search.toLowerCase()) ||
      (p.reference || p.ref || "").toLowerCase().includes(search.toLowerCase()) ||
      (p.notes || "").toLowerCase().includes(search.toLowerCase());
    const matchStatus =
      statusFilter === "All" ||
      (statusFilter === "Paid" && (p.status === "Paid" || p.status === "Completed")) ||
      (statusFilter === "Pending" && p.status === "Pending") ||
      (statusFilter === "Unpaid" && (p.status === "Unpaid" || p.status === "Not Paid"));
    const matchDateFrom = !dateFrom || p.date >= dateFrom;
    const matchDateTo = !dateTo || p.date <= dateTo;
    return matchSearch && matchStatus && matchDateFrom && matchDateTo;
  });

  const detail = selectedRow !== null ? filtered[selectedRow] : null;
  const filteredTotal = filtered.reduce((s, p) => s + (p.amount || 0), 0);
  const totalBooking = totals.collected + totals.open + totals.outstanding;
  const collectedPerc = totalBooking > 0 ? Math.round((totals.collected / totalBooking) * 100) : 0;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={`${styles.modal} ${styles.modalFull}`} onClick={(e) => e.stopPropagation()}>
        {/* ── Header ── */}
        <div className={styles.vaHeader}>
          <div className={styles.vaHeaderLeft}>
            <div className={styles.vaHeaderIcon}>
              <CreditCard size={20} />
            </div>
            <div>
              <h3 className={styles.vaHeaderTitle}>Client Payments</h3>
              <p className={styles.vaHeaderSub}>All client payments for this booking</p>
            </div>
          </div>
          <div className={styles.vaHeaderRight}>
            <span className={styles.vaHeaderCount}>{payments.length} transactions</span>
            <button className={styles.modalClose} onClick={onClose}><X size={18} /></button>
          </div>
        </div>

        {/* ── Summary Row ── */}
        <div className={styles.vaStatsRow}>
          {/* Collection progress */}
          <div className={styles.vaProgressCard}>
            <div className={styles.vaProgressHeader}>
              <span className={styles.vaProgressTitle}>Collection Progress</span>
              <span className={styles.vaProgressPerc}>{collectedPerc}%</span>
            </div>
            <div className={styles.vaProgressTrack}>
              <div className={styles.vaProgressFill} style={{ width: `${collectedPerc}%` }} />
            </div>
            <div className={styles.vaProgressLegend}>
              <span className={styles.vaProgressLegendItem}>
                <span className={styles.vaLegendDot} style={{ background: "#16a34a" }} />
                Collected {fmt(totals.collected)}
              </span>
              <span className={styles.vaProgressLegendItem}>
                <span className={styles.vaLegendDot} style={{ background: "#f59e0b" }} />
                Pending {fmt(totals.open)}
              </span>
              <span className={styles.vaProgressLegendItem}>
                <span className={styles.vaLegendDot} style={{ background: "#dc2626" }} />
                Outstanding {fmt(totals.outstanding)}
              </span>
            </div>
          </div>

          {/* Stat cards */}
          <div className={styles.vaStatCards}>
            <div className={styles.vaStatCard}>
              <div className={`${styles.vaStatDot} ${styles.vaStatDotGreen}`} />
              <div>
                <span className={styles.vaStatLabel}>Collected</span>
                <span className={`${styles.vaStatVal} ${styles.metricGreen}`}>{fmt(totals.collected)}</span>
              </div>
            </div>
            <div className={styles.vaStatCard}>
              <div className={`${styles.vaStatDot} ${styles.vaStatDotOrange}`} />
              <div>
                <span className={styles.vaStatLabel}>Open</span>
                <span className={`${styles.vaStatVal} ${styles.metricOrange}`}>{fmt(totals.open)}</span>
              </div>
            </div>
            <div className={styles.vaStatCard}>
              <div className={`${styles.vaStatDot} ${styles.vaStatDotBlue}`} />
              <div>
                <span className={styles.vaStatLabel}>Refunds</span>
                <span className={styles.vaStatVal}>{fmt(totals.refunds)}</span>
              </div>
            </div>
            <div className={styles.vaStatCard}>
              <div className={`${styles.vaStatDot} ${styles.vaStatDotRed}`} />
              <div>
                <span className={styles.vaStatLabel}>Outstanding</span>
                <span className={`${styles.vaStatVal} ${styles.metricRed}`}>{fmt(totals.outstanding)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div className={styles.vaToolbar}>
          <div className={styles.vaToolbarLeft}>
            <div className={styles.viewAllSearchWrap}>
              <Search size={14} className={styles.viewAllSearchIcon} />
              <input
                className={styles.viewAllSearch}
                placeholder="Search method, reference, notes..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className={styles.viewAllFilters}>
              {["All", "Paid", "Pending", "Unpaid"].map((s) => (
                <button
                  key={s}
                  className={`${styles.viewAllFilterBtn} ${statusFilter === s ? styles.viewAllFilterActive : ""}`}
                  onClick={() => setStatusFilter(s)}
                >
                  {s}
                  {s !== "All" && <span className={styles.filterCount}>{countByStatus(s)}</span>}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.vaToolbarRight}>
            <div className={styles.dateRangeWrap}>
              <Calendar size={13} className={styles.dateRangeIcon} />
              <input type="date" className={styles.dateInput} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              <span className={styles.dateRangeSep}>—</span>
              <input type="date" className={styles.dateInput} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            <button className={styles.viewAllExportBtn}><Download size={13} /> Export</button>
          </div>
        </div>

        {/* ── Table + Detail ── */}
        <div className={styles.viewAllContent}>
          <div className={styles.viewAllTableArea}>
            <table className={styles.vaTable}>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th style={{ width: 100 }}>Date</th>
                  <th style={{ width: 140 }}>Type</th>
                  <th style={{ width: 160 }}>Payment Method</th>
                  <th>Reference / Transaction ID</th>
                  <th style={{ width: 120 }}>Amount</th>
                  <th style={{ width: 130 }}>Linked To</th>
                  <th style={{ width: 110 }}>Status</th>
                  <th>Notes</th>
                  <th style={{ width: 110 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={10} className={styles.emptyRow}>
                      <div className={styles.emptyState}>
                        <div className={styles.emptyStateIcon}><CreditCard size={24} /></div>
                        <span className={styles.emptyStateTitle}>No payments found</span>
                        <span className={styles.emptyStateText}>Try adjusting your search or filters</span>
                      </div>
                    </td>
                  </tr>
                )}
                {filtered.map((p, i) => {
                  const mi = getMethodIcon(p.method);
                  const isSelected = selectedRow === i;
                  return (
                    <tr
                      key={p.id || i}
                      className={`${isSelected ? styles.selectedTableRow : ""} ${i % 2 === 1 ? styles.vaTableRowAlt : ""}`}
                      onClick={() => setSelectedRow(isSelected ? null : i)}
                    >
                      <td className={styles.rowNum}>{i + 1}</td>
                      <td><span className={styles.dateCell}>{formatDate(p.date)}</span></td>
                      <td>{paymentTypeBadge(p.paymentTypeDisplay || "Incoming Payment", p.directionDisplay || "Incoming", true)}</td>
                      <td>
                        <div className={styles.methodCell}>
                          <span className={styles.methodBadge} style={{ background: mi.bg, color: mi.color }}>{mi.icon}</span>
                          <span className={styles.methodName}>{p.method}</span>
                        </div>
                      </td>
                      <td><span className={styles.mono}>{p.reference || p.ref}</span></td>
                      <td className={styles.amountCell}>{fmt(p.amount)}</td>
                      <td><span className={styles.linkedToBadge}>{p.linkedTo || "Booking Payment"}</span></td>
                      <td>{statusBadge(p.status)}</td>
                      <td className={styles.notesCell}><span title={p.notes}>{p.notes || "—"}</span></td>
                      <td>
                        <div className={styles.rowActions}>
                          <button className={styles.rowActionBtn} title="View" onClick={(e) => { e.stopPropagation(); setSelectedRow(i); }}><Eye size={14} /></button>
                          <button className={styles.rowActionBtn} title="Edit" onClick={(e) => { e.stopPropagation(); setEditingPayment(p); }}><Pencil size={14} /></button>
                          <button className={`${styles.rowActionBtn} ${styles.rowActionDanger}`} title="Delete" onClick={(e) => { e.stopPropagation(); onDeletePayment && onDeletePayment(p); }}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {filtered.length > 0 && (
                <tfoot>
                  <tr className={styles.vaTableFooter}>
                    <td colSpan={5}>
                      <span className={styles.vaFooterLabel}>{filtered.length} payment{filtered.length !== 1 ? "s" : ""}</span>
                    </td>
                    <td className={styles.amountCell}>
                      <strong>{fmt(filteredTotal)}</strong>
                    </td>
                    <td colSpan={4} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* ── Detail Side Panel ── */}
          {detail && (
            <div className={styles.vaDetailPanel}>
              <div className={styles.vaDetailHeader}>
                <span className={styles.vaDetailTitle}>Payment Details</span>
                <button className={styles.detailClose} onClick={() => setSelectedRow(null)}><X size={14} /></button>
              </div>
              <div className={styles.vaDetailBody}>
                {/* Amount hero */}
                <div className={styles.vaDetailHero}>
                  <span className={styles.vaDetailHeroLabel}>Amount</span>
                  <span className={styles.vaDetailHeroAmount}>{fmt(detail.amount)}</span>
                  <div className={styles.vaDetailHeroStatus}>{statusBadge(detail.status)}</div>
                </div>

                {/* Info rows */}
                <div className={styles.vaDetailInfoCard}>
                  <div className={styles.vaDetailInfoRow}>
                    <Calendar size={14} className={styles.vaDetailInfoIcon} />
                    <span className={styles.vaDetailInfoLabel}>Date</span>
                    <span className={styles.vaDetailInfoValue}>{formatDate(detail.date)}</span>
                  </div>
                  <div className={styles.vaDetailInfoRow}>
                    <CreditCard size={14} className={styles.vaDetailInfoIcon} />
                    <span className={styles.vaDetailInfoLabel}>Method</span>
                    <span className={styles.vaDetailInfoValue}>
                      <span className={styles.methodBadge} style={{ background: getMethodIcon(detail.method).bg, color: getMethodIcon(detail.method).color, width: 20, height: 20, fontSize: 10, borderRadius: 5 }}>{getMethodIcon(detail.method).icon}</span>
                      {detail.method}
                    </span>
                  </div>
                  <div className={styles.vaDetailInfoRow}>
                    <FileText size={14} className={styles.vaDetailInfoIcon} />
                    <span className={styles.vaDetailInfoLabel}>Reference</span>
                    <span className={`${styles.vaDetailInfoValue} ${styles.mono}`}>{detail.reference || detail.ref}</span>
                  </div>
                  <div className={styles.vaDetailInfoRow}>
                    <Info size={14} className={styles.vaDetailInfoIcon} />
                    <span className={styles.vaDetailInfoLabel}>Linked To</span>
                    <span className={styles.vaDetailInfoValue}>{detail.linkedTo || "Booking Payment"}</span>
                  </div>
                  {detail.createdBy && (
                    <div className={styles.vaDetailInfoRow}>
                      <ExternalLink size={14} className={styles.vaDetailInfoIcon} />
                      <span className={styles.vaDetailInfoLabel}>Created By</span>
                      <span className={styles.vaDetailInfoValue}>{detail.createdBy}</span>
                    </div>
                  )}
                </div>

                {/* Notes */}
                {detail.notes && (
                  <div className={styles.vaDetailNotes}>
                    <span className={styles.vaDetailNotesLabel}>Notes</span>
                    <p className={styles.vaDetailNotesText}>{detail.notes}</p>
                  </div>
                )}

                {/* Actions */}
                <div className={styles.vaDetailActions}>
                  <button className={styles.vaDetailActionPrimary}><Printer size={13} /> Print Receipt</button>
                  <div className={styles.vaDetailActionRow}>
                    <button className={styles.detailActionBtn}><Copy size={13} /> Copy Ref</button>
                    <button className={`${styles.detailActionBtn} ${styles.detailActionBtnWarn}`}><RefreshCw size={13} /> Refund</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className={styles.vaFooter}>
          <span className={styles.viewAllCount}>Showing {filtered.length} of {payments.length} payments</span>
          <button className={styles.btnCancel} onClick={onClose}>Close</button>
        </div>
      </div>

      {/* Edit Client Payment Modal */}
      <AddClientPaymentModal
        open={!!editingPayment}
        onClose={() => setEditingPayment(null)}
        editData={editingPayment}
        bookingRef={bookingRef}
        customerName={customerName}
        onSave={(data) => {
          console.log("Updated client payment:", data);
          setEditingPayment(null);
        }}
      />
    </div>
  );
};

/* ===================================================================
   ViewAllSupplierPaymentsModal
   =================================================================== */
const PRODUCT_TYPE_ICONS = {
  Flight: { icon: Plane, bg: "#eef3ff", color: "#2563eb" },
  Hotel: { icon: Hotel, bg: "#fef3e2", color: "#d97706" },
  Transfer: { icon: Bus, bg: "#e8f5ee", color: "#0d7a4f" },
  Insurance: { icon: Shield, bg: "#fce8e8", color: "#c42b2b" },
};

const ViewAllSupplierPaymentsModal = ({ open, onClose, payments, totals, bookingComponents = [], bookingTravelers = [], bookingAssignments = [], onDeletePayment, bookingRef = "" }) => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selectedRow, setSelectedRow] = useState(null);
  const [viewMode, setViewMode] = useState("payments");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [editingPayment, setEditingPayment] = useState(null);

  if (!open) return null;

  const countByStatus = (st) =>
    payments.filter((p) =>
      st === "Paid" ? p.status === "Paid" :
        st === "Partial" ? p.status === "Partially Paid" || p.status === "Partial" :
          st === "Unpaid" ? p.status === "Not Paid" || p.status === "Unpaid" : true
    ).length;

  const filtered = payments.filter((p) => {
    const matchSearch =
      !search ||
      (p.supplier || "").toLowerCase().includes(search.toLowerCase()) ||
      (p.reference || "").toLowerCase().includes(search.toLowerCase()) ||
      (p.notes || "").toLowerCase().includes(search.toLowerCase());
    const matchStatus =
      statusFilter === "All" ||
      (statusFilter === "Paid" && p.status === "Paid") ||
      (statusFilter === "Partial" && (p.status === "Partially Paid" || p.status === "Partial")) ||
      (statusFilter === "Unpaid" && (p.status === "Not Paid" || p.status === "Unpaid"));
    const matchDateFrom = !dateFrom || p.date >= dateFrom;
    const matchDateTo = !dateTo || p.date <= dateTo;
    return matchSearch && matchStatus && matchDateFrom && matchDateTo;
  });

  const detail = selectedRow !== null && viewMode === "payments" ? filtered[selectedRow] : null;

  const paidPerc = totals.cost > 0 ? Math.round((totals.paid / totals.cost) * 100) : 0;

  const productGroups = (() => {
    const groups = {};
    filtered.forEach((p) => {
      const products = p.products || [];
      if (products.length === 0) {
        // Fallback: use component field as product type
        const key = p.component || "general";
        if (!groups[key]) groups[key] = { type: key, products: [], totalAmount: 0, totalPaid: 0, suppliers: new Set() };
        groups[key].products.push({ type: key, paymentRef: p.reference, supplier: p.supplier, amount: p.amount, paidAmount: p.amount, status: p.status, date: p.date });
        groups[key].totalAmount += p.amount || 0;
        groups[key].totalPaid += p.amount || 0;
        groups[key].suppliers.add(p.supplier || "");
      } else {
        products.forEach((pr) => {
          const key = pr.type;
          if (!groups[key]) groups[key] = { type: pr.type, products: [], totalAmount: 0, totalPaid: 0, suppliers: new Set() };
          groups[key].products.push({ ...pr, paymentRef: p.reference, supplier: p.supplier, amount: p.amount / products.length, paidAmount: (p.paidAmount || 0) / products.length, status: p.status, date: p.date });
          groups[key].totalAmount += p.amount / products.length;
          groups[key].totalPaid += (p.paidAmount || 0) / products.length;
          groups[key].suppliers.add(p.supplier || "");
        });
      }
    });
    return Object.values(groups);
  })();

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={`${styles.modal} ${styles.modalFull}`} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.viewAllHeaderLeft}>
            <div className={`${styles.viewAllIcon} ${styles.viewAllIconBlue}`}>
              <ArrowUpRight size={18} />
            </div>
            <div>
              <h3 className={styles.modalTitle}>All Supplier Payments</h3>
              <p className={styles.modalSubtitle}>
                Complete history of all supplier payments for this booking.
              </p>
            </div>
          </div>
          <button className={styles.modalClose} onClick={onClose}><X size={18} /></button>
        </div>

        {/* Summary cards */}
        <div className={styles.viewAllSummaryCards}>
          <div className={styles.summaryStatCard}>
            <div className={`${styles.summaryStatIcon} ${styles.summaryStatIconBlue}`}>
              <ArrowUpRight size={14} />
            </div>
            <div className={styles.summaryStatContent}>
              <span className={styles.summaryStatLabel}>Supplier Cost</span>
              <span className={styles.summaryStatValue}>{fmt(totals.cost)}</span>
            </div>
          </div>
          <div className={styles.summaryStatCard}>
            <div className={`${styles.summaryStatIcon} ${styles.summaryStatIconGreen}`}>
              <Check size={14} />
            </div>
            <div className={styles.summaryStatContent}>
              <span className={styles.summaryStatLabel}>Total Paid</span>
              <span className={`${styles.summaryStatValue} ${styles.metricGreen}`}>{fmt(totals.paid)}</span>
              <div className={styles.miniProgress}>
                <div className={styles.miniProgressTrack}>
                  <div className={styles.miniProgressFill} style={{ width: `${paidPerc}%` }} />
                </div>
                <span className={styles.miniProgressLabel}>{paidPerc}%</span>
              </div>
            </div>
          </div>
          <div className={styles.summaryStatCard}>
            <div className={`${styles.summaryStatIcon} ${styles.summaryStatIconOrange}`}>
              <CreditCard size={14} />
            </div>
            <div className={styles.summaryStatContent}>
              <span className={styles.summaryStatLabel}>Open Payables</span>
              <span className={`${styles.summaryStatValue} ${styles.metricOrange}`}>{fmt(totals.openPayables)}</span>
            </div>
          </div>
          <div className={`${styles.summaryStatCard} ${styles.summaryStatCardHighlight}`}>
            <div className={`${styles.summaryStatIcon} ${styles.summaryStatIconRed}`}>
              <Info size={14} />
            </div>
            <div className={styles.summaryStatContent}>
              <span className={styles.summaryStatLabel}>Outstanding</span>
              <span className={`${styles.summaryStatValue} ${styles.metricRed}`}>{fmt(totals.outstanding)}</span>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className={styles.viewAllToolbar}>
          <div className={styles.viewAllSearchWrap}>
            <Search size={14} className={styles.viewAllSearchIcon} />
            <input
              className={styles.viewAllSearch}
              placeholder="Search by supplier, reference or notes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className={styles.viewAllFilters}>
            {["All", "Paid", "Partial", "Unpaid"].map((s) => (
              <button
                key={s}
                className={`${styles.viewAllFilterBtn} ${statusFilter === s ? styles.viewAllFilterActive : ""}`}
                onClick={() => setStatusFilter(s)}
              >
                {s}
                {s !== "All" && <span className={styles.filterCount}>{countByStatus(s)}</span>}
              </button>
            ))}
          </div>
          <div className={styles.viewAllViewTabs}>
            <button className={`${styles.viewTabBtn} ${viewMode === "payments" ? styles.viewTabActive : ""}`} onClick={() => { setViewMode("payments"); setSelectedRow(null); }}>By Payments</button>
            <button className={`${styles.viewTabBtn} ${viewMode === "products" ? styles.viewTabActive : ""}`} onClick={() => { setViewMode("products"); setSelectedRow(null); }}>By Products</button>
          </div>
          <div className={styles.dateRangeWrap}>
            <Calendar size={13} className={styles.dateRangeIcon} />
            <input type="date" className={styles.dateInput} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <span className={styles.dateRangeSep}>—</span>
            <input type="date" className={styles.dateInput} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <button className={styles.viewAllExportBtn}><Download size={13} /> Export</button>
        </div>

        {/* Main content */}
        <div className={styles.viewAllContent}>
          {viewMode === "payments" ? (
            <>
              <div className={styles.viewAllTableArea}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Supplier</th>
                      <th>Products</th>
                      <th>Reference</th>
                      <th>Method</th>
                      <th>Amount</th>
                      <th>Paid</th>
                      <th>Balance</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={12} className={styles.emptyRow}>
                          <div className={styles.emptyState}>
                            <div className={`${styles.emptyStateIcon} ${styles.emptyStateIconBlue}`}><ArrowUpRight size={24} /></div>
                            <span className={styles.emptyStateTitle}>No supplier payments found</span>
                            <span className={styles.emptyStateText}>Try adjusting your search or filters</span>
                          </div>
                        </td>
                      </tr>
                    )}
                    {filtered.map((p, i) => {
                      const balance = p.amount - p.paidAmount;
                      return (
                        <tr
                          key={p.id || i}
                          className={selectedRow === i ? styles.selectedTableRow : ""}
                          onClick={() => setSelectedRow(selectedRow === i ? null : i)}
                        >
                          <td className={styles.rowNum}>{i + 1}</td>
                          <td><span className={styles.dateCell}>{formatDate(p.date)}</span></td>
                          <td>{paymentTypeBadge(p.paymentTypeDisplay || "Outgoing Payment", p.directionDisplay || "Outgoing")}</td>
                          <td>
                            <div className={styles.supplierCell}>
                              <span className={styles.supplierLogo}>{p.supplier.charAt(0)}</span>
                              <span className={styles.supplierName}>{p.supplier}</span>
                            </div>
                          </td>
                          <td>
                            <div className={styles.productsList}>
                              {p.products.map((pr, j) => (
                                <span key={j} className={styles.productPill}>
                                  {pr.type === "Flight" && <Plane size={10} />}
                                  {pr.type === "Hotel" && <Hotel size={10} />}
                                  {pr.type === "Transfer" && <Bus size={10} />}
                                  {pr.type === "Insurance" && <Shield size={10} />}
                                  {pr.name}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td><span className={styles.mono}>{p.reference}</span></td>
                          <td><span className={styles.methodSmall}>{p.method || "—"}</span></td>
                          <td className={styles.amountCell}>{fmt(p.amount)}</td>
                          <td className={`${styles.amountCell} ${styles.metricGreen}`}>{fmt(p.paidAmount)}</td>
                          <td className={`${styles.amountCell} ${balance > 0 ? styles.metricOrange : styles.metricGreen}`}>
                            {fmt(balance)}
                          </td>
                          <td>{statusBadge(p.status)}</td>
                          <td>
                            <div className={styles.rowActions}>
                              <button className={styles.rowActionBtn} title="View" onClick={(e) => { e.stopPropagation(); setSelectedRow(i); }}><Eye size={14} /></button>
                              <button className={styles.rowActionBtn} title="Edit" onClick={(e) => { e.stopPropagation(); setEditingPayment(p); }}><Pencil size={14} /></button>
                              <button className={`${styles.rowActionBtn} ${styles.rowActionDanger}`} title="Delete" onClick={(e) => { e.stopPropagation(); onDeletePayment && onDeletePayment(p); }}><Trash2 size={14} /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {filtered.length > 0 && (
                    <tfoot>
                      <tr className={styles.summaryFooterRow}>
                        <td colSpan={7} className={styles.footerLabel}>Totals ({filtered.length} payments)</td>
                        <td className={styles.amountCell}><strong>{fmt(filtered.reduce((s, p) => s + p.amount, 0))}</strong></td>
                        <td className={`${styles.amountCell} ${styles.metricGreen}`}><strong>{fmt(filtered.reduce((s, p) => s + p.paidAmount, 0))}</strong></td>
                        <td className={`${styles.amountCell} ${styles.metricOrange}`}><strong>{fmt(filtered.reduce((s, p) => s + (p.amount - p.paidAmount), 0))}</strong></td>
                        <td colSpan={2} />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {/* Detail side panel */}
              {detail && (
                <div className={styles.viewAllDetail}>
                  <div className={styles.detailHeader}>
                    <span className={styles.detailTitle}>Payment #{filtered.indexOf(detail) + 1}</span>
                    <button className={styles.detailClose} onClick={() => setSelectedRow(null)}><X size={14} /></button>
                  </div>
                  <div className={styles.detailBody}>
                    <div className={styles.detailAmountBlock}>
                      <span className={styles.detailAmountLabel}>Total Amount</span>
                      <span className={styles.detailAmount}>{fmt(detail.amount)}</span>
                      {statusBadge(detail.status)}
                    </div>
                    <div className={styles.detailProgressBar}>
                      <div className={styles.detailProgressTrack}>
                        <div className={styles.detailProgressFill} style={{ width: `${Math.min(100, (detail.paidAmount / detail.amount) * 100)}%` }} />
                      </div>
                      <div className={styles.detailProgressLabels}>
                        <span>Paid: {fmt(detail.paidAmount)}</span>
                        <span>Remaining: {fmt(detail.amount - detail.paidAmount)}</span>
                      </div>
                    </div>
                    <div className={styles.detailGrid}>
                      <div className={styles.detailItem}>
                        <Calendar size={13} />
                        <div>
                          <span className={styles.detailItemLabel}>Payment Date</span>
                          <span className={styles.detailItemValue}>{formatDate(detail.date)}</span>
                        </div>
                      </div>
                      <div className={styles.detailItem}>
                        <CreditCard size={13} />
                        <div>
                          <span className={styles.detailItemLabel}>Supplier</span>
                          <span className={styles.detailItemValue}>{detail.supplier}</span>
                        </div>
                      </div>
                      <div className={styles.detailItem}>
                        <FileText size={13} />
                        <div>
                          <span className={styles.detailItemLabel}>Reference</span>
                          <span className={`${styles.detailItemValue} ${styles.mono}`}>{detail.reference}</span>
                        </div>
                      </div>
                      <div className={styles.detailItem}>
                        <CreditCard size={13} />
                        <div>
                          <span className={styles.detailItemLabel}>Payment Method</span>
                          <span className={styles.detailItemValue}>{detail.method || "—"}</span>
                        </div>
                      </div>
                    </div>
                    {detail.notes && (
                      <div className={styles.detailNotes}>
                        <span className={styles.detailItemLabel}>Notes</span>
                        <p className={styles.detailNotesText}>{detail.notes}</p>
                      </div>
                    )}
                    {/* Payment Allocation breakdown — how this payment is divided
                        internally (Per Room / Per Traveler). Informational only. */}
                    {detail.allocation?.mode === "per_room" && (
                      <div className={styles.detailProductsSection}>
                        <span className={styles.detailItemLabel}>
                          Allocation — Per Room{detail.allocation.productName ? ` · ${detail.allocation.productName}` : ""}
                        </span>
                        <div className={styles.detailProductsList}>
                          {(detail.allocation.rooms || []).map((r, j) => (
                            <div key={j} className={styles.detailProductCard}>
                              <span className={styles.detailProductIcon}><Hotel size={13} /></span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <span className={styles.detailProductName}>
                                  Room {j + 1}{r.label ? ` — ${r.label}` : ""}
                                </span>
                                <span className={styles.detailProductType}>Complete room amount</span>
                              </div>
                              <span style={{ fontWeight: 700, fontSize: 12.5, color: "#0f172a", whiteSpace: "nowrap" }}>
                                {fmt(r.amount)}
                              </span>
                            </div>
                          ))}
                        </div>
                        {(detail.allocation.travelers || []).length > 0 && (
                          <span style={{ display: "block", marginTop: 6, fontSize: 11, color: "#64748b" }}>
                            Travelers (info only): {(detail.allocation.travelers || []).map((t) => t.name).filter(Boolean).join(", ")}
                          </span>
                        )}
                      </div>
                    )}
                    {detail.allocation?.mode === "per_traveler" && (detail.allocation.travelers || []).length > 0 && (
                      <div className={styles.detailProductsSection}>
                        <span className={styles.detailItemLabel}>
                          Allocation — Per Traveler{detail.allocation.productName ? ` · ${detail.allocation.productName}` : ""}
                        </span>
                        <div className={styles.detailProductsList}>
                          {(detail.allocation.travelers || []).map((t, j) => (
                            <div key={j} className={styles.detailProductCard}>
                              <span className={styles.detailProductIcon}><Users size={13} /></span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <span className={styles.detailProductName}>{t.name || `Traveler ${j + 1}`}</span>
                                {t.direction && (
                                  <span className={styles.detailProductType}>
                                    {t.direction === "out" ? "Outbound" : t.direction === "in" ? "Inbound" : t.direction}
                                  </span>
                                )}
                              </div>
                              <span style={{ fontWeight: 700, fontSize: 12.5, color: "#0f172a", whiteSpace: "nowrap" }}>
                                {fmt(t.amount)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className={styles.detailProductsSection}>
                      <span className={styles.detailItemLabel}>Products Covered</span>
                      <div className={styles.detailProductsList}>
                        {detail.products.map((pr, j) => (
                          <div key={j} className={styles.detailProductCard}>
                            <span className={styles.detailProductIcon}>
                              {pr.type === "Flight" && <Plane size={13} />}
                              {pr.type === "Hotel" && <Hotel size={13} />}
                              {pr.type === "Transfer" && <Bus size={13} />}
                              {pr.type === "Insurance" && <Shield size={13} />}
                            </span>
                            <div>
                              <span className={styles.detailProductName}>{pr.name}</span>
                              <span className={styles.detailProductType}>{pr.type}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className={styles.detailActions}>
                      <button className={styles.detailActionBtn}><Printer size={13} /> Print</button>
                      <button className={styles.detailActionBtn}><Copy size={13} /> Copy Ref</button>
                      <button className={`${styles.detailActionBtn} ${styles.detailActionBtnWarn}`}><RefreshCw size={13} /> Refund</button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* ── By Products View ── */
            <div className={styles.viewAllTableArea}>
              <div className={styles.productGroupsWrap}>
                {productGroups.length === 0 && (
                  <div className={styles.emptyState}>
                    <div className={`${styles.emptyStateIcon} ${styles.emptyStateIconBlue}`}><ArrowUpRight size={24} /></div>
                    <span className={styles.emptyStateTitle}>No products found</span>
                    <span className={styles.emptyStateText}>Try adjusting your search or filters</span>
                  </div>
                )}
                {productGroups.map((group) => {
                  const TypeIcon = PRODUCT_TYPE_ICONS[group.type]?.icon || CreditCard;
                  const iconStyle = PRODUCT_TYPE_ICONS[group.type] || { bg: "#f5f6f8", color: "#6b7280" };
                  const groupPerc = group.totalAmount > 0 ? Math.round((group.totalPaid / group.totalAmount) * 100) : 0;
                  return (
                    <div key={group.type} className={styles.productGroupCard}>
                      <div className={styles.productGroupHeader}>
                        <div className={styles.productGroupHeaderLeft}>
                          <span className={styles.productGroupIcon} style={{ background: iconStyle.bg, color: iconStyle.color }}>
                            <TypeIcon size={16} />
                          </span>
                          <div>
                            <span className={styles.productGroupTitle}>{group.type}s</span>
                            <span className={styles.productGroupMeta}>{group.products.length} item{group.products.length !== 1 ? "s" : ""} · {group.suppliers.size} supplier{group.suppliers.size !== 1 ? "s" : ""}</span>
                          </div>
                        </div>
                        <div className={styles.productGroupTotals}>
                          <div className={styles.productGroupTotalItem}>
                            <span className={styles.productGroupTotalLabel}>Total</span>
                            <span className={styles.productGroupTotalValue}>{fmt(group.totalAmount)}</span>
                          </div>
                          <div className={styles.productGroupTotalItem}>
                            <span className={styles.productGroupTotalLabel}>Paid</span>
                            <span className={`${styles.productGroupTotalValue} ${styles.metricGreen}`}>{fmt(group.totalPaid)}</span>
                          </div>
                          <div className={styles.productGroupTotalItem}>
                            <span className={styles.productGroupTotalLabel}>Balance</span>
                            <span className={`${styles.productGroupTotalValue} ${(group.totalAmount - group.totalPaid) > 0 ? styles.metricOrange : styles.metricGreen}`}>{fmt(group.totalAmount - group.totalPaid)}</span>
                          </div>
                        </div>
                      </div>
                      <div className={styles.productGroupProgress}>
                        <div className={styles.miniProgressTrack}>
                          <div className={styles.miniProgressFill} style={{ width: `${groupPerc}%` }} />
                        </div>
                        <span className={styles.miniProgressLabel}>{groupPerc}% paid</span>
                      </div>
                      <div className={styles.productGroupItems}>
                        {group.products.map((pr, j) => (
                          <div key={j} className={styles.productGroupItem}>
                            <span className={styles.productGroupItemName}>{pr.name}</span>
                            <span className={styles.productGroupItemSupplier}>{pr.supplier}</span>
                            <span className={styles.productGroupItemRef}>{pr.paymentRef}</span>
                            <span className={styles.productGroupItemAmount}>{fmt(pr.amount)}</span>
                            <span>{statusBadge(pr.status)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <span className={styles.viewAllCount}>
            {viewMode === "payments"
              ? `Showing ${filtered.length} of ${payments.length} payments`
              : `${productGroups.length} product groups`}
          </span>
          <button className={styles.btnCancel} onClick={onClose}>Close</button>
        </div>
      </div>

      {/* Edit Supplier Payment Modal */}
      <AddSupplierPaymentModal
        open={!!editingPayment}
        onClose={() => setEditingPayment(null)}
        editData={editingPayment}
        bookingComponents={bookingComponents}
        bookingTravelers={bookingTravelers}
        bookingAssignments={bookingAssignments}
        bookingRef={bookingRef}
        onSave={(data) => {
          console.log("Updated supplier payment:", data);
          setEditingPayment(null);
        }}
      />
    </div>
  );
};

/* ===================================================================
   Main PaymentsTab
   =================================================================== */
const PaymentsTab = ({ booking, bookingId: bookingIdProp, onPaymentChange }) => {
  // Tab-wise layout: the Payments tab shows ONE section at a time (Client ⇄ Supplier).
  const [activeSection, setActiveSection] = useState("client");
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [viewAllClientOpen, setViewAllClientOpen] = useState(false);
  const [viewAllSupplierOpen, setViewAllSupplierOpen] = useState(false);
  const [editingClientPayment, setEditingClientPayment] = useState(null);
  const [editingSupplierPayment, setEditingSupplierPayment] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, type: 'client'|'supplier', label }
  const { showToast } = useToast();

  const bookingRef = booking?.ref || "";
  const customerName = (booking?.customer?.name && booking.customer.name !== "—" ? booking.customer.name : null)
    || booking?.customerName || "";
  const { data: apiPayments, loading: paymentsLoading, error: paymentsError, refetch } = useGetBookingPayments(bookingRef);
  const { execute: createPayment, loading: creating } = useCreatePayment();
  const { execute: updatePayment, loading: updating } = useUpdatePayment();
  const { execute: deletePaymentApi, loading: deleting } = useDeletePayment();

  // Post-booking modifications carry supplier costs that must be payable to suppliers.
  const { execute: fetchModifications } = useGetProductModifications();
  const [bookingModifications, setBookingModifications] = useState([]);
  const bookingIdForMods = bookingIdProp || booking?.id || booking?.ref;
  useEffect(() => {
    let active = true;
    if (!bookingIdForMods) { setBookingModifications([]); return undefined; }
    fetchModifications(bookingIdForMods).then((res) => {
      if (!active) return;
      const data = res?.success
        ? (Array.isArray(res.data) ? res.data : (res.data?.modifications || res.data?.items || []))
        : [];
      setBookingModifications(data);
    });
    return () => { active = false; };
  }, [bookingIdForMods, fetchModifications]);

  // The Finance payments API (Payment model) is the canonical source — it is what
  // create/edit/delete all target. We must NOT fall back to the deprecated
  // `bookingPayments` table (passed in as `legacyPayments`): those rows carry no
  // `id`, so the Delete button silently no-ops (`handleDeletePayment` bails on
  // `!payment?.id`) and Edit routes to create. Always reflect the API rows.
  const clientPayments = apiPayments?.client || [];
  const supplierPayments = apiPayments?.supplier || [];

  const summary = apiPayments?.summary || null;
  // ── SUNSKY Client Payment Logic ──────────────────────────────────────────────
  //   Gross Client Payments − Transaction Fees = Net Bank Received
  //   Pool = Net Bank Received − client payouts − client refunds (completed only)
  //   Applied to Booking  = min(pool, booking total)
  //   Customer Credit     = max(0, pool − booking total)
  //   Outstanding Balance = max(0, booking total − pool)   ← can NEVER be negative
  // The backend summary is authoritative; local fallbacks mirror it for safety.
  const num = (v) => parseFloat(v ?? 0) || 0;
  const grossOf = (p) => num(p.amount);
  const feeOf = (p) => num(p.transactionFee ?? p.transaction_fee);
  const isIncoming = (p) => (p.direction || "").toLowerCase() === "incoming" || (p.paymentType || "").includes("incoming");
  const isOutgoing = (p) => (p.direction || "").toLowerCase() === "outgoing" || (p.paymentType || "").includes("outgoing");
  const isCompleted = (p) => p.status === "Completed" || p.status === "Paid";
  const completedClientIn = clientPayments.filter((p) => isIncoming(p) && isCompleted(p));
  const clientGross = summary?.client?.gross ?? completedClientIn.reduce((s, p) => s + grossOf(p), 0);
  const clientFees = summary?.client?.fees ?? completedClientIn.reduce((s, p) => s + feeOf(p), 0);
  const clientNetBank = summary?.client?.netBankReceived ?? (clientGross - clientFees);
  const clientPayouts = summary?.client?.outgoing ?? clientPayments
    .filter((p) => isOutgoing(p) && isCompleted(p))
    .reduce((s, p) => s + grossOf(p), 0);
  const clientRefunds = summary?.client?.refunds ?? 0;
  // DUAL POOL — a client-covered transaction cost is part of what the client owes:
  //   netPool = gross − covered costs − payouts − refunds → drives OUTSTANDING + CREDIT
  // (gross totals are shown via the "Gross Client Payments" tile as clientGross.)
  const clientNetPool = summary?.client?.netPool ?? (clientNetBank - clientPayouts - clientRefunds);
  const clientPending = summary?.client?.pending ?? clientPayments
    .filter((p) => p.status === "Pending")
    .reduce((s, p) => s + grossOf(p), 0);
  const clientTotal = summary?.client?.total ?? clientPayments.filter(isIncoming).reduce((s, p) => s + grossOf(p), 0);
  const clientOpen = Math.max(0, clientTotal - clientGross - clientPending);

  const compTypeById = Object.fromEntries((booking?.components || []).map((c) => [String(c.id), c.type]));
  const modNum = (v) => parseFloat(v ?? 0) || 0;

  // The backend's grandTotal (apiPayments.bookingGrandTotal) already includes non-baked
  // product modifications (Flight, etc.) via recalculateBookingTotals + createProductModification.
  // Hotel & Transfer mods are baked into productDetails pricing. Only traveler modifications
  // (name changes, etc.) are NOT folded into grandTotal by the backend, so add those here.
  // For CANCEL_TRAVELER we add the retained supplierCost only — refund is a separate
  // outflow (customer credit / refund due) and never reduces the booking total. When
  // the client overpays, credit emerges naturally as netPool − bookingGrandTotal.
  const travelerModClientNet = (booking?.travelerModifications || []).reduce((s, m) => {
    if ((m.actionType ?? m.action_type) === "CANCEL_TRAVELER") {
      return s + modNum(m.supplierCost);
    }
    return s + modNum(m.totalModificationAmount ?? m.total_modification_amount);
  }, 0);

  const baseBookingGrandTotal = parseFloat(apiPayments?.bookingGrandTotal || booking?.totalPrice || 0);
  const bookingGrandTotal = baseBookingGrandTotal + travelerModClientNet;
  // Applied / Credit / Outstanding under the DUAL rule, derived from the mods-aware
  // booking total. Outstanding + Applied use the NET pool (a client-covered
  // transaction cost is part of what the client owes, so it lifts outstanding; a
  // Sunsky-borne cost is absorbed). Credit also uses the NET pool. Both clamped at 0.
  const round2 = (v) => Math.round(v * 100) / 100;
  const clientApplied = round2(Math.max(0, Math.min(clientNetPool, bookingGrandTotal)));
  const clientCredit = bookingGrandTotal > 0 ? round2(Math.max(0, clientNetPool - bookingGrandTotal)) : 0;
  const clientOutstanding = round2(Math.max(0, bookingGrandTotal - clientNetPool));
  // `clientPool` kept for downstream displays (Collection Progress, View-All) = applied.
  const clientPool = clientApplied;

  // ── "Amount to Pay" saldo colour rule ───────────────────────────────────────
  //   RED    open balance AND the earliest product starts within 3 weeks (or already started)
  //   ORANGE we owe the customer money back → negative saldo (e.g. € -100,00)
  //   GREEN  saldo is € 0,00 (fully paid)
  //   BLUE   open balance AND the earliest product starts in more than 3 weeks
  const _startTimes = (booking?.components || []).flatMap((c) => {
    const r = c?._raw || {};
    let pd = r.productDetails;
    if (typeof pd === "string") { try { pd = JSON.parse(pd); } catch { pd = {}; } }
    pd = pd || {};
    const acc = [];
    const push = (d) => { if (d) { const t = new Date(d).getTime(); if (!Number.isNaN(t)) acc.push(t); } };
    push(r.serviceStartDate);
    push(pd.checkIn); push(pd.departureDate); push(pd.pickupDate);
    push(pd.insuranceStartDate || pd.validFrom); push(pd.visaIssueDate || pd.applicationDate);
    (pd.legs || []).forEach((l) => push(l?.departureDate));
    push(pd.schedule?.pickupDate);
    push(pd.arrivalLeg?.schedule?.pickupDate);
    push(pd.departureLeg?.schedule?.pickupDate);
    return acc;
  });
  const firstStartMs = _startTimes.length ? Math.min(..._startTimes) : null;
  const todayMs = new Date(new Date().toDateString()).getTime();
  const THREE_WEEKS_MS = 21 * 24 * 60 * 60 * 1000;
  // started already OR starts within 3 weeks
  const startUrgent = firstStartMs != null && firstStartMs - todayMs < THREE_WEEKS_MS;

  const owesClient = clientCredit > 0.005;        // we must refund the customer
  const balanceOpen = clientOutstanding > 0.005;  // customer still owes us
  const amountToPay = owesClient ? -clientCredit : clientOutstanding; // signed saldo
  const amountToPayColor = owesClient ? "orange"   // we owe the client money back → orange (negative)
    : !balanceOpen ? "green"                        // saldo € 0,00, fully paid → green
    : startUrgent ? "red"                           // open + starts within 3 weeks / started → red
    : "blue";                                       // open + starts in more than 3 weeks → blue
  const SALDO_VAL_CLASS = { red: styles.saldoRed, orange: styles.saldoOrange, green: styles.saldoGreen, blue: styles.saldoBlue };
  const SALDO_TILE_CLASS = { red: styles.saldoTileRed, orange: styles.saldoTileOrange, green: styles.saldoTileGreen, blue: styles.saldoTileBlue };

  // Chronological ledger for the client table (DUAL rule): each completed incoming
  // payment settles the booking by its NET (gross − the cost the CLIENT covers), so
  // a client-covered cost leaves more open (the client owes it). When Sunsky bears
  // the cost the full gross settles the booking. The row's credit is what's left of
  // that net once the booking is covered. A payout re-opens the debt by its gross.
  const coveredFeeOf = (p) => (p.clientCoversFee !== false ? feeOf(p) : 0);
  const clientLedger = (() => {
    const map = {};
    const sorted = [...clientPayments].sort(
      (a, b) => (new Date(a.date || 0) - new Date(b.date || 0)) || ((a.id || 0) - (b.id || 0))
    );
    let remaining = Math.max(0, bookingGrandTotal);
    sorted.forEach((p) => {
      const key = p.id ?? `${p.date}-${p.amount}`;
      if (isIncoming(p)) {
        const net = round2(Math.max(0, grossOf(p) - feeOf(p)));
        if (!isCompleted(p)) { map[key] = { net, applied: null, credit: null }; return; }
        const settle = round2(Math.max(0, grossOf(p) - coveredFeeOf(p))); // net toward booking
        const applied = round2(Math.max(0, Math.min(settle, remaining)));
        remaining = round2(remaining - applied);
        // Leftover of the settling net beyond what covered the booking.
        const credit = round2(Math.max(0, settle - applied));
        map[key] = { net, applied, credit };
      } else {
        map[key] = { net: null, applied: null, credit: null };
        if (isCompleted(p)) remaining = round2(remaining + grossOf(p));
      }
    });
    return map;
  })();

  // Supplier cost is cancellation-aware: a fully cancelled product no longer owes its
  // ORIGINAL cost — Sunsky owes the supplier only the cancellation cost (the supplier
  // penalty). This keeps "Supplier Amount"/"Outstanding to Supplier" in step with the
  // Pricing tab Purchase Price and the backend totalCost. If the supplier was already
  // overpaid, the excess surfaces as Supplier Credit (a refund due back to us).
  const componentTotalCost = (booking?.components || []).reduce((s, c) => {
    const raw = c._raw || {};
    const isCancelled = c.status === "Cancelled" || raw.productStatus === "Cancelled";
    if (isCancelled) {
      const pd = typeof raw.productDetails === "string"
        ? (() => { try { return JSON.parse(raw.productDetails); } catch { return {}; } })()
        : (raw.productDetails || {});
      return s + (Number(pd.cancellation?.cost) || 0);
    }
    return s + (c.costPrice || 0);
  }, 0);

  // Post-booking modifications add to what we owe suppliers and MUST be part of the
  // supplier cost / outstanding. Hotel & Transfer product mods are already baked into
  // the component costPrice by the backend (productDetails.pricing.roomPricing /
  // pricing.costPrice), so we add ONLY the non-baked ones — Flight/other product mods
  // plus ALL traveler mods — to avoid double-counting. Uses gross supplierCost, the
  // same payable the SupplierPaymentWizard charges against.
  const productModSupplierCost = (bookingModifications || []).reduce((s, m) => {
    const t = compTypeById[String(m.productId ?? m.product?.id ?? m.componentId)];
    if (t === "Hotel" || t === "Transfer") return s; // already baked into component cost
    return s + Math.max(0, modNum(m.supplierCost ?? m.supplier_cost));
  }, 0);
  const travelerModSupplierCost = (booking?.travelerModifications || []).reduce(
    (s, m) => s + Math.max(0, modNum(m.supplierCost ?? m.supplier_cost)), 0
  );
  const modificationsSupplierCost = productModSupplierCost + travelerModSupplierCost;

  const baseSupplierCost = componentTotalCost > 0
    ? componentTotalCost
    : (summary?.supplier?.total ?? supplierPayments.reduce((s, p) => s + (p.amount || 0), 0));
  // Supplier Amount = what we owe suppliers for this booking (invoice total).
  const supplierTotalCost = baseSupplierCost + modificationsSupplierCost;

  // ── SUNSKY Supplier Payment Fee Logic ────────────────────────────────────────
  //   fee paid by Sunsky   → bank outgoing = amount + fee, supplier nets amount
  //   fee paid by supplier → bank outgoing = amount, supplier nets amount − fee
  //   Applied to Supplier = amount either way, minus refunds received back.
  //   Outstanding to Supplier = max(0, Supplier Amount − Applied) — never negative;
  //   over-allocation shows as Supplier Credit. Sunsky-paid fees are bank expenses.
  const completedSupplier = supplierPayments.filter(isCompleted);
  const supplierTotalPaid = summary?.supplier?.paid ?? completedSupplier.reduce((s, p) => s + grossOf(p), 0);
  const supplierFees = summary?.supplier?.fees ?? completedSupplier.reduce((s, p) => s + feeOf(p), 0);
  const supplierBankOutgoing = summary?.supplier?.bankOutgoing ?? completedSupplier.reduce(
    (s, p) => s + grossOf(p) + ((p.feePaidBy || p.fee_paid_by) === "supplier" ? 0 : feeOf(p)), 0
  );
  const supplierNetReceived = summary?.supplier?.netReceived ?? completedSupplier.reduce(
    (s, p) => s + ((p.feePaidBy || p.fee_paid_by) === "supplier" ? grossOf(p) - feeOf(p) : grossOf(p)), 0
  );
  const supplierRefunds = summary?.supplier?.refunds ?? 0;
  const supplierApplied = summary?.supplier?.applied ?? round2(supplierTotalPaid - supplierRefunds);
  const supplierOpenPayables = Math.max(0, round2(supplierTotalCost - supplierTotalPaid));
  const supplierOutstanding = round2(Math.max(0, supplierTotalCost - supplierApplied));
  const supplierCredit = round2(Math.max(0, supplierApplied - supplierTotalCost));

  const handleSaveClientPayment = async (formData) => {
    setSaveError(null);
    const apiPayload = {
      payment_type: toApiPaymentType(formData.paymentType) || "incoming_payment",
      booking_reference: bookingRef,
      customer_name: (booking?.customer?.name && booking.customer.name !== "—" ? booking.customer.name : null)
        || booking?.customerName || "Client",
      customer_email: (booking?.customer?.email && booking.customer.email !== "—" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(booking.customer.email) ? booking.customer.email : null)
        || (booking?.customerEmail && booking.customerEmail !== "—" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(booking.customerEmail) ? booking.customerEmail : "noemail@placeholder.com"),
      account_id: formData.accountId ? parseInt(formData.accountId) : null,
      account_number: formData.accountNumber || null,
      payment_method: toApiMethod(formData.method),
      amount: parseFloat(formData.amount) || 0,
      currency: formData.currency || "EUR",
      payment_date: formData.date,
      status: toApiStatus(formData.status) || "pending",
      notes: formData.notes || null,
      linked_to: toApiLinkedTo(formData.linkedTo),
      // SUNSKY client payment logic fields (gross / fee / net + provider refs)
      gross_amount: formData.grossAmount != null ? parseFloat(formData.grossAmount) || 0 : null,
      transaction_fee: formData.transactionFee != null ? parseFloat(formData.transactionFee) || 0 : null,
      // Is the client covering the transaction cost? (default true → cost not refunded)
      client_covers_fee: formData.clientCoversFee !== false,
      net_received: formData.netReceived != null ? parseFloat(formData.netReceived) || 0 : null,
      payment_provider: formData.provider || null,
      settlement_reference: formData.settlementReference || null,
    };

    let result;
    if (editingClientPayment?.id) {
      const { payment_type, booking_reference, customer_name, customer_email, ...updateData } = apiPayload;
      result = await updatePayment(editingClientPayment.id, updateData);
    } else {
      result = await createPayment(apiPayload);
    }

    if (result.success) {
      showToast(editingClientPayment?.id ? "Client payment updated" : "Client payment added", "success");
      refetch();
      onPaymentChange?.();
      setClientModalOpen(false);
      setEditingClientPayment(null);
    } else {
      showToast(result.message || "Failed to save client payment", "error");
    }
  };

  const handleSaveSupplierPayment = async (formData) => {
    setSaveError(null);
    const apiPayload = {
      // Honour the selected type: a "Supplier Refund" must be saved as a refund
      // (money coming back from the supplier), not forced to an outgoing payment.
      payment_type: toApiPaymentType(formData.paymentType) || "outgoing_payment",
      booking_reference: bookingRef,
      supplier_name: formData.supplierName || "Supplier",
      supplier_email: formData.supplierEmail || null,
      component: formData.component || "general",
      account_id: formData.accountId ? parseInt(formData.accountId) : null,
      account_number: formData.accountNumber || null,
      payment_method: toApiMethod(formData.method),
      amount: parseFloat(formData.totalAmount || formData.amount) || 0,
      currency: formData.currency || "EUR",
      payment_date: formData.date,
      status: toApiStatus(formData.status) || "pending",
      transaction_id: formData.transactionId || formData.reference || null,
      airline_pnr: formData.airlinePnr || null,
      // The wizard has no separate "Supplier Reference No" box — the older inline form did,
      // and this line still reads only that. So every payment recorded through the wizard
      // stored NULL here, and every screen that prints a supplier reference (the payment and
      // refund view modals, the transaction detail) had nothing to print.
      //
      // Falling back to `reference` is not a guess about what that box holds: it is
      // auto-populated FROM THE SELECTED PRODUCT with the supplier's own identifiers — the
      // outbound and inbound PNRs on a flight, the confirmation number on a hotel — which is
      // exactly what a supplier reference is. A value typed into the dedicated field still
      // wins, so the inline form is unaffected.
      supplier_reference_no: formData.supplierReferenceNo || formData.reference || null,
      notes: formData.notes || null,
      // SUNSKY supplier fee logic: fee + who bears it (sunsky → fee on top of the
      // amount as a bank expense; supplier → deducted from what they receive).
      transaction_fee: formData.transactionFee != null ? parseFloat(formData.transactionFee) || 0 : 0,
      fee_paid_by: formData.feePaidBy || null,
      // Payment Allocation — how the amount is divided internally (Per Room for
      // hotels by default, Per Traveler otherwise). Informational only.
      allocation: formData.allocation || null,
    };

    let result;
    if (editingSupplierPayment?.id) {
      const { payment_type, booking_reference, ...updateData } = apiPayload;
      result = await updatePayment(editingSupplierPayment.id, updateData);
    } else {
      result = await createPayment(apiPayload);
    }

    if (result.success) {
      showToast(editingSupplierPayment?.id ? "Supplier payment updated" : "Supplier payment added", "success");
      refetch();
      onPaymentChange?.();
      setSupplierModalOpen(false);
      setEditingSupplierPayment(null);
    } else {
      showToast(result.message || "Failed to save supplier payment", "error");
    }
  };

  const handleDeletePayment = (payment, type) => {
    if (!payment?.id) return;
    const label = type === 'client'
      ? `${payment.method || 'Client'} payment of ${fmt(payment.amount)}`
      : `${payment.supplier || payment.supplierName || 'Supplier'} payment of ${fmt(payment.amount)}`;
    setDeleteTarget({ id: payment.id, type, label });
  };

  const confirmDeletePayment = async () => {
    if (!deleteTarget?.id) return;
    const result = await deletePaymentApi(deleteTarget.id);
    if (result.success) {
      showToast(`${deleteTarget.type === 'client' ? 'Client' : 'Supplier'} payment deleted successfully`, "success");
      refetch();
      onPaymentChange?.();
    } else {
      showToast(result.message || "Failed to delete payment", "error");
    }
    setDeleteTarget(null);
  };

  const isBusy = creating || updating || deleting;

  return (
    <div className={styles.paymentsWrap}>
      {/* ── Section Title ── */}
      <div className={styles.sectionHead}>
        <div className={styles.sectionIcon}>
          <CreditCard size={16} />
        </div>
        <span className={styles.sectionLabel}>Payments</span>
        {paymentsLoading && <Loader2 size={14} className={styles.spinIcon} />}
      </div>

      {/* ── Error banner ── */}
      {(paymentsError || saveError) && (
        <div className={styles.errorBanner}>
          <AlertCircle size={14} />
          <span>{saveError || paymentsError}</span>
          <button onClick={() => setSaveError(null)} className={styles.errorClose}><X size={12} /></button>
        </div>
      )}

      {/* ════════════ Section Switcher: Client ⇄ Supplier ════════════ */}
      <div className={styles.paySectionTabs}>
        <button
          className={`${styles.paySectionTab} ${activeSection === "client" ? styles.paySectionTabActive : ""}`}
          onClick={() => setActiveSection("client")}
        >
          <ArrowDownLeft size={14} />
          Client Payments
          <span className={styles.paySectionTabCount}>{clientPayments.length}</span>
        </button>
        <button
          className={`${styles.paySectionTab} ${activeSection === "supplier" ? styles.paySectionTabActive : ""}`}
          onClick={() => setActiveSection("supplier")}
        >
          <ArrowUpRight size={14} />
          Supplier Payments
          <span className={styles.paySectionTabCount}>{supplierPayments.length}</span>
        </button>
      </div>

      {/* ════════════ CLIENT PAYMENTS SECTION ════════════ */}
      {activeSection === "client" && (
        <>
          {/* Summary tiles — SUNSKY client payment logic */}
          <div className={styles.tileGrid}>
            <div className={styles.payTile} title="The total amount paid by the customer, including payment costs.">
              <span className={styles.payTileLabel}><ArrowDownLeft size={12} /> Gross Client Payments <Info size={11} className={styles.tileInfoIcon} /></span>
              <span className={styles.payTileValue}>{fmt(clientGross)}</span>
              <span className={styles.payTileSub}>Total paid by customer</span>
            </div>
            <div className={styles.payTile} title="Costs charged by the payment provider / terminal (e.g. Worldline, Stripe).">
              <span className={styles.payTileLabel}>Transaction Costs <Info size={11} className={styles.tileInfoIcon} /></span>
              <span className={styles.payTileValue}>{fmt(clientFees)}</span>
              <span className={styles.payTileSub}>Payment provider costs</span>
            </div>
            <div className={styles.payTile} title="Gross − costs: the amount actually received on our bank account. Used for CODA/CAMT reconciliation.">
              <span className={styles.payTileLabel}>Net Bank Received <Info size={11} className={styles.tileInfoIcon} /></span>
              <span className={styles.payTileValue}>{fmt(clientNetBank)}</span>
              <span className={styles.payTileSub}>Received on our bank</span>
            </div>
            <div className={styles.payTile} title="The amount used to settle the booking value.">
              <span className={styles.payTileLabel}>Applied to Booking <Info size={11} className={styles.tileInfoIcon} /></span>
              <span className={styles.payTileValue}>{fmt(clientApplied)}</span>
              <span className={styles.payTileSub}>Used to pay the booking</span>
            </div>
            <div className={`${styles.payTile} ${amountToPay < -0.005 ? styles.payTileClientCredit : amountToPay > 0.005 ? styles.payTileDanger : ""}`} title="Signed balance with the customer: a positive amount is still to be collected; a negative amount is a refund due to the customer (e.g. an overpayment or a paid-then-cancelled booking).">
              <span className={styles.payTileLabel}>Outstanding <Info size={11} className={styles.tileInfoIcon} /></span>
              <span className={`${styles.payTileValue} ${amountToPay < -0.005 ? styles.tileValueClientCredit : amountToPay > 0.005 ? styles.tileValueDanger : styles.tileValueOk}`}>{fmt(amountToPay)}</span>
              <span className={styles.payTileSub}>{amountToPay < -0.005 ? "Refund due to customer" : amountToPay > 0.005 ? "To be received from customer" : "Fully settled"}</span>
            </div>
          </div>

          {/* Payment details table */}
          <div className={styles.tableSection}>
            <div className={styles.tableHead}>
              <div className={styles.tableHeadLeft}>
                <CreditCard size={15} className={styles.tableHeadIconInline} />
                <span>Payment Details</span>
              </div>
              <div className={styles.tableHeadActions}>
                <button className={styles.viewAllBtn} onClick={() => setViewAllClientOpen(true)}>
                  View All <ExternalLink size={12} />
                </button>
                <LoadingButton
                  className={styles.addPaymentBtn}
                  loading={creating && !editingClientPayment}
                  loadingText="Saving…"
                  icon={<Plus size={14} />}
                  disabled={isBusy}
                  onClick={() => { setEditingClientPayment(null); setClientModalOpen(true); }}
                >
                  Add Client Payment
                </LoadingButton>
              </div>
            </div>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Method / Provider</th>
                    <th>Reference</th>
                    <th className={styles.thRight}>Gross</th>
                    <th className={styles.thRight}>Txn Cost</th>
                    <th className={styles.thRight}>Net Received</th>
                    <th className={styles.thRight}>Applied</th>
                    <th className={styles.thRight}>Credit</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clientPayments.length === 0 && !paymentsLoading && (
                    <tr>
                      <td colSpan={11} className={styles.emptyRow}>
                        <div className={styles.emptyState}>
                          <div className={styles.emptyStateIcon}><CreditCard size={24} /></div>
                          <span className={styles.emptyStateTitle}>No client payments yet</span>
                          <span className={styles.emptyStateText}>Click "Add Client Payment" to record one</span>
                        </div>
                      </td>
                    </tr>
                  )}
                  {clientPayments.map((p, i) => {
                    const mi = getMethodIcon(p.method);
                    const led = clientLedger[p.id ?? `${p.date}-${p.amount}`] || {};
                    const incoming = isIncoming(p);
                    return (
                      <tr key={p.id || i}>
                        <td>{formatDate(p.date)}</td>
                        <td>{paymentTypeBadge(p.paymentTypeDisplay || "Incoming Payment", p.directionDisplay || "Incoming", true)}</td>
                        <td>
                          <div className={styles.methodCell}>
                            <span className={styles.methodBadge} style={{ background: mi.bg, color: mi.color }}>{mi.icon}</span>
                            <div className={styles.methodCellText}>
                              <span>{p.method}</span>
                              {p.provider && <span className={styles.cellSub}>{p.provider}</span>}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={styles.mono} title={p.settlementReference ? `Settlement: ${p.settlementReference}` : undefined}>
                            {p.reference || p.ref || "—"}
                          </span>
                        </td>
                        <td className={`${styles.amountCell} ${!incoming ? styles.amountNeg : ""}`}>
                          {incoming ? fmt(p.amount) : `− ${fmt(p.amount)}`}
                        </td>
                        <td className={`${styles.amountCell} ${styles.feeCell}`}>{incoming && feeOf(p) > 0 ? fmt(feeOf(p)) : "—"}</td>
                        <td className={styles.amountCell}>{incoming ? fmt(led.net ?? Math.max(0, grossOf(p) - feeOf(p))) : "—"}</td>
                        <td className={styles.amountCell}>{incoming && led.applied != null ? fmt(led.applied) : "—"}</td>
                        <td className={`${styles.amountCell} ${led.credit > 0.005 ? styles.creditCell : styles.feeCell}`}>
                          {incoming && led.credit != null && led.credit > 0.005 ? fmt(led.credit) : "—"}
                        </td>
                        <td>{statusBadge(p.status)}</td>
                        <td>
                          <div className={styles.rowActions}>
                            <button className={styles.rowActionBtn} title="Edit" disabled={isBusy} onClick={() => { setEditingClientPayment(p); setClientModalOpen(true); }}><Pencil size={14} /></button>
                            <button className={`${styles.rowActionBtn} ${styles.rowActionDanger}`} title="Delete" disabled={isBusy} onClick={() => handleDeletePayment(p, 'client')}><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* How it works */}
          <div className={styles.hiwCard}>
            <div className={styles.hiwTitle}><Info size={13} /> How it works</div>
            <div className={styles.hiwStrip}>
              <span className={styles.hiwChip}><span className={styles.hiwChipLabel}>Gross Payment</span><b>{fmt(clientGross)}</b></span>
              <span className={styles.hiwOp}>−</span>
              <span className={styles.hiwChip}><span className={styles.hiwChipLabel}>Transaction Cost</span><b>{fmt(clientFees)}</b></span>
              <span className={styles.hiwOp}>=</span>
              <span className={`${styles.hiwChip} ${styles.hiwChipAccent}`}><span className={styles.hiwChipLabel}>Net Received</span><b>{fmt(clientNetBank)}</b></span>
              <span className={styles.hiwOp}>→</span>
              <span className={styles.hiwChip}><span className={styles.hiwChipLabel}>Applied to Booking</span><b>{fmt(clientApplied)}</b></span>
              <span className={styles.hiwOp}>+</span>
              <span className={`${styles.hiwChip} ${styles.hiwChipCredit}`}><span className={styles.hiwChipLabel}>Customer Credit</span><b>{fmt(clientCredit)}</b></span>
            </div>
            {clientCredit > 0.005 && (
              <div className={styles.creditHint}>
                Customer Credit of <strong>{fmt(clientCredit)}</strong> can be used for a future booking or refunded to the customer.
              </div>
            )}
          </div>
        </>
      )}

      {/* ════════════ SUPPLIER PAYMENTS SECTION ════════════ */}
      {activeSection === "supplier" && (
        <>
          {/* Summary tiles — SUNSKY supplier payment fee logic */}
          <div className={styles.tileGrid}>
            <div className={styles.payTile} title="The total amount we owe the supplier(s) for this booking, according to the invoice / purchase costs.">
              <span className={styles.payTileLabel}><ArrowUpRight size={12} /> Supplier Amount <Info size={11} className={styles.tileInfoIcon} /></span>
              <span className={styles.payTileValue}>{fmt(supplierTotalCost)}</span>
              <span className={styles.payTileSub}>Supplier invoice amount</span>
            </div>
            <div className={styles.payTile} title="Costs charged by the payment provider for making the outgoing payments.">
              <span className={styles.payTileLabel}>Transaction Costs <Info size={11} className={styles.tileInfoIcon} /></span>
              <span className={styles.payTileValue}>{fmt(supplierFees)}</span>
              <span className={styles.payTileSub}>Payment provider costs</span>
            </div>
            <div className={styles.payTile} title="The total amount leaving our bank account (supplier amounts + fees paid by Sunsky).">
              <span className={styles.payTileLabel}>Total Bank Outgoing <Info size={11} className={styles.tileInfoIcon} /></span>
              <span className={styles.payTileValue}>{fmt(supplierBankOutgoing)}</span>
              <span className={styles.payTileSub}>Leaving our bank account</span>
            </div>
            <div className={styles.payTile} title="The amount the supplier receives on their bank account after fees.">
              <span className={styles.payTileLabel}>Net Received by Supplier <Info size={11} className={styles.tileInfoIcon} /></span>
              <span className={styles.payTileValue}>{fmt(supplierNetReceived)}</span>
              <span className={styles.payTileSub}>Supplier receives after fees</span>
            </div>
            <div className={styles.payTile} title="The amount used to settle supplier invoices (payments minus refunds received back).">
              <span className={styles.payTileLabel}>Applied to Supplier <Info size={11} className={styles.tileInfoIcon} /></span>
              <span className={styles.payTileValue}>{fmt(supplierApplied)}</span>
              <span className={styles.payTileSub}>Settles supplier invoices</span>
            </div>
            {supplierCredit > 0.005 ? (
              <div className={`${styles.payTile} ${styles.payTileCredit}`} title="More than the supplier amount has been allocated to the supplier.">
                <span className={styles.payTileLabel}>Supplier Credit <Info size={11} className={styles.tileInfoIcon} /></span>
                <span className={`${styles.payTileValue} ${styles.tileValueCredit}`}>{fmt(supplierCredit)}</span>
                <span className={styles.payTileSub}>Overpaid to supplier</span>
              </div>
            ) : (
              <div className={`${styles.payTile} ${supplierOutstanding > 0.005 ? styles.payTileDanger : ""}`} title="The amount still to be paid to the supplier. It can never be negative.">
                <span className={styles.payTileLabel}>Outstanding to Supplier <Info size={11} className={styles.tileInfoIcon} /></span>
                <span className={`${styles.payTileValue} ${supplierOutstanding > 0.005 ? styles.tileValueDanger : styles.tileValueOk}`}>{fmt(supplierOutstanding)}</span>
                <span className={styles.payTileSub}>Amount still to be paid</span>
              </div>
            )}
          </div>

          {/* Rule strip */}
          <div className={styles.infoStrip}>
            <Info size={14} />
            <span>
              <strong>Outstanding to supplier can never be negative.</strong> Transaction fees paid by
              Sunsky are bank expenses — they are <strong>not</strong> supplier credit.
            </span>
          </div>

          {/* Supplier payment details table */}
          <div className={styles.tableSection}>
            <div className={styles.tableHead}>
              <div className={styles.tableHeadLeft}>
                <CreditCard size={15} className={styles.tableHeadIconInline} />
                <span>Supplier Payment Details</span>
              </div>
              <div className={styles.tableHeadActions}>
                <button className={styles.viewAllBtn} onClick={() => setViewAllSupplierOpen(true)}>
                  View All <ExternalLink size={12} />
                </button>
                <LoadingButton
                  className={`${styles.addPaymentBtn} ${styles.addSupplierBtn}`}
                  loading={creating && !editingSupplierPayment}
                  loadingText="Saving…"
                  icon={<Plus size={14} />}
                  disabled={isBusy}
                  onClick={() => { setEditingSupplierPayment(null); setSupplierModalOpen(true); }}
                >
                  Add Supplier Payment
                </LoadingButton>
              </div>
            </div>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Supplier</th>
                    <th>Reference</th>
                    <th className={styles.thRight}>Supplier Amount</th>
                    <th className={styles.thRight}>Cost</th>
                    <th>Cost Paid By</th>
                    <th className={styles.thRight}>Bank Outgoing</th>
                    <th className={styles.thRight}>Net to Supplier</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {supplierPayments.length === 0 && !paymentsLoading && (
                    <tr>
                      <td colSpan={11} className={styles.emptyRow}>
                        <div className={styles.emptyState}>
                          <div className={`${styles.emptyStateIcon} ${styles.emptyStateIconBlue}`}><ArrowUpRight size={24} /></div>
                          <span className={styles.emptyStateTitle}>No supplier payments yet</span>
                          <span className={styles.emptyStateText}>Click "Add Supplier Payment" to record one</span>
                        </div>
                      </td>
                    </tr>
                  )}
                  {supplierPayments.map((p, i) => {
                    const fee = feeOf(p);
                    const bearer = p.feePaidBy || p.fee_paid_by || null;
                    const isRefundRow = (p.paymentType || "").includes("refund");
                    const rowBank = isRefundRow ? 0 : grossOf(p) + (bearer === "supplier" ? 0 : fee);
                    const rowNet = isRefundRow ? 0 : (bearer === "supplier" ? Math.max(0, grossOf(p) - fee) : grossOf(p));
                    return (
                      <tr key={p.id || i}>
                        <td>{formatDate(p.date)}</td>
                        <td>{paymentTypeBadge(p.paymentTypeDisplay || "Outgoing Payment", p.directionDisplay || "Outgoing")}</td>
                        <td>
                          <div className={styles.supplierCell}>
                            <span className={styles.supplierLogo}>{(p.supplier || p.supplierName || "S").charAt(0)}</span>
                            <div className={styles.methodCellText}>
                              <span className={styles.supplierName}>{p.supplier || p.supplierName || "—"}</span>
                              {p.component && (
                                <span className={styles.cellSub}>
                                  {p.component}
                                  {p.allocation?.mode === "per_room"
                                    ? ` · Per Room (${(p.allocation.rooms || []).length})`
                                    : p.allocation?.mode === "per_traveler"
                                    ? " · Per Traveler"
                                    : ""}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td><span className={styles.mono}>{p.reference || "—"}</span></td>
                        <td className={`${styles.amountCell} ${isRefundRow ? styles.amountNeg : ""}`}>
                          {isRefundRow ? `− ${fmt(p.amount)}` : fmt(p.amount)}
                        </td>
                        <td className={`${styles.amountCell} ${styles.feeCell}`}>{!isRefundRow && fee > 0 ? fmt(fee) : "—"}</td>
                        <td>
                          {!isRefundRow && fee > 0 && bearer
                            ? <span className={bearer === "supplier" ? styles.bearerBadgeSupplier : styles.bearerBadgeSunsky}>{bearer === "supplier" ? "Supplier" : "Sunsky"}</span>
                            : <span className={styles.feeCell}>—</span>}
                        </td>
                        <td className={styles.amountCell}>{!isRefundRow ? fmt(rowBank) : "—"}</td>
                        <td className={styles.amountCell}>{!isRefundRow ? fmt(rowNet) : "—"}</td>
                        <td>{statusBadge(p.status)}</td>
                        <td>
                          <div className={styles.rowActions}>
                            <button className={styles.rowActionBtn} title="Edit" disabled={isBusy} onClick={() => { setEditingSupplierPayment(p); setSupplierModalOpen(true); }}><Pencil size={14} /></button>
                            <button className={`${styles.rowActionBtn} ${styles.rowActionDanger}`} title="Delete" disabled={isBusy} onClick={() => handleDeletePayment(p, 'supplier')}><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* How it works (fee paid by Sunsky is the recommended default) */}
          <div className={styles.hiwCard}>
            <div className={styles.hiwTitle}><Info size={13} /> How it works</div>
            <div className={styles.hiwStrip}>
              <span className={styles.hiwChip}><span className={styles.hiwChipLabel}>Supplier Amount</span><b>{fmt(supplierTotalPaid)}</b></span>
              <span className={styles.hiwOp}>+</span>
              <span className={styles.hiwChip}><span className={styles.hiwChipLabel}>Costs paid by Sunsky</span><b>{fmt(Math.max(0, supplierBankOutgoing - supplierTotalPaid))}</b></span>
              <span className={styles.hiwOp}>=</span>
              <span className={`${styles.hiwChip} ${styles.hiwChipAccent}`}><span className={styles.hiwChipLabel}>Total Bank Outgoing</span><b>{fmt(supplierBankOutgoing)}</b></span>
              <span className={styles.hiwOp}>→</span>
              <span className={styles.hiwChip}><span className={styles.hiwChipLabel}>Net to Supplier</span><b>{fmt(supplierNetReceived)}</b></span>
              <span className={styles.hiwOp}>→</span>
              <span className={styles.hiwChip}><span className={styles.hiwChipLabel}>Applied to Supplier</span><b>{fmt(supplierApplied)}</b></span>
            </div>
            <div className={styles.creditHint}>
              A fee paid by Sunsky is an additional bank cost — it is <strong>not</strong> an overpayment to the supplier.
              When the supplier bears the cost, they receive less than the invoice amount.
            </div>
          </div>
        </>
      )}

      {/* ── Modals ── */}
      <AddClientPaymentModal
        open={clientModalOpen}
        onClose={() => { setClientModalOpen(false); setEditingClientPayment(null); }}
        editData={editingClientPayment}
        saving={creating || updating}
        onSave={handleSaveClientPayment}
        bookingRef={bookingRef}
        outstandingAmount={clientOutstanding}
        bookingTotal={bookingGrandTotal || clientTotal}
        clientPaid={clientPool}
        customerName={customerName}
        refundDue={clientCredit}
      />
      <SupplierPaymentWizard
        open={supplierModalOpen}
        onClose={() => { setSupplierModalOpen(false); setEditingSupplierPayment(null); }}
        editData={editingSupplierPayment}
        bookingComponents={booking?.components || []}
        bookingTravelers={booking?.travelers || []}
        bookingAssignments={booking?.assignments || []}
        bookingSSRs={booking?.ssrs || []}
        bookingModifications={bookingModifications}
        bookingTravelerModifications={booking?.travelerModifications || []}
        existingPayments={supplierPayments}
        saving={creating || updating}
        onSave={handleSaveSupplierPayment}
      />
      <ViewAllClientPaymentsModal
        open={viewAllClientOpen}
        onClose={() => setViewAllClientOpen(false)}
        payments={clientPayments}
        totals={{
          collected: clientPool,
          open: clientOpen,
          refunds: clientRefunds,
          outstanding: clientOutstanding,
        }}
        onDeletePayment={(p) => handleDeletePayment(p, 'client')}
        bookingRef={bookingRef}
        customerName={customerName}
      />
      <ViewAllSupplierPaymentsModal
        open={viewAllSupplierOpen}
        onClose={() => setViewAllSupplierOpen(false)}
        payments={supplierPayments}
        bookingComponents={booking?.components || []}
        bookingTravelers={booking?.travelers || []}
        bookingAssignments={booking?.assignments || []}
        totals={{
          cost: supplierTotalCost,
          paid: supplierTotalPaid,
          openPayables: supplierOpenPayables,
          refunds: supplierRefunds,
          outstanding: supplierOutstanding,
        }}
        onDeletePayment={(p) => handleDeletePayment(p, 'supplier')}
        bookingRef={bookingRef}
      />

      {/* ── Delete Confirmation Modal ── */}
      <DeleteConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={`Delete ${deleteTarget?.type === 'client' ? 'Client' : 'Supplier'} Payment`}
        message={`Are you sure you want to delete this ${deleteTarget?.type === 'client' ? 'client' : 'supplier'} payment${deleteTarget?.label ? ` (${deleteTarget.label})` : ''}? This action cannot be undone.`}
        onConfirm={confirmDeletePayment}
        loading={deleting}
        confirmLabel="Delete Payment"
      />
    </div>
  );
};

export default PaymentsTab;
